import type { ServiceAreaSlug } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";

export type ServiceSlug =
  | "roof-replacement"
  | "roof-repair"
  | "asphalt-shingle-roofing"
  | "metal-roofing"
  | "flat-roofing"
  | "roof-inspection-maintenance"
  | "flashing-roof-penetration-repair"
  | "shingle-spot-repair"
  | "gutter-cleaning-downspout-flush"
  | "storm-tarping-emergency-dry-in"
  | "skylight-leak-flashing-repair"
  | "chimney-flashing-repair"
  | "gutter-replacement"
  | "gutter-guard-installation"
  | "roof-ventilation-upgrade"
  | "fascia-soffit-repair-replacement"
  | "cedar-shake-repair-restoration"
  | "flat-low-slope-roof-repair"
  | "metal-roof-repair-maintenance"
  | "cedar-shake-roof-replacement"
  | "moss-removal-roof-gutter"
  | "roof-health-check-maintenance";

export type ServiceContentTranslation = Partial<
  Omit<
    ServicePage,
    "slug" | "image" | "path" | "category" | "relatedServiceSlugs" | "relatedAreaSlugs"
  >
>;

export type ServiceAreaContentTranslation = {
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  faqs: readonly { question: string; answer: string }[];
};

export const services = {
  "roof-replacement": {
    title: "تعویض شینگل آسفالتی",
    shortTitle: "تعویض آسفالتی",
    eyebrow: "تعویض و اجرای دوبارهٔ سقف شیبدار",
    description:
      "کندن کامل شینگل آسفالتی، زیرلایه، شینگل، یکپارچه‌سازی فلاشینگ و دفع ضایعات برای خانه‌های شیبدار مترو ونکوور.",
    definition:
      "تعویض شینگل آسفالتی یک تعویض کامل سقف شیبدار است که پوشش شینگل موجود را برمی‌دارد، نیازهای زیرسازی و زیرلایه را برطرف می‌کند، سیستم شینگل جدید نصب می‌کند و فلاشینگ و نفوذها را دوباره یکپارچه می‌کند.",
    metaTitle: "تعویض شینگل آسفالتی ونکوور بریتیش کلمبیا | رولین روفینگ",
    metaDescription:
      "تعویض شینگل آسفالتی معماری برای خانه‌های مترو ونکوور. کندن، زیرلایه، یکپارچه‌سازی فلاشینگ و شرح کار مکتوب پس از ارزیابی.",
    forWhom:
      "مالکانی که شینگل‌ها به‌طور گسترده فرسوده‌اند، مکرر تعمیر شده‌اند، یا دیگر با برنامه باقی‌مانده خانه سازگار نیستند.",
    warningSigns: [
      "قوز، ترک یا از دست رفتن گسترده دانه",
      "چند سطح تعمیرشده که همچنان خراب می‌شوند",
      "شینگل شکننده که دیگر درزبند نمی‌شود",
      "دره، شیروانی و لبه‌های پیر",
      "کار بازسازی که تعویض کامل سقف را منسجم می‌کند",
    ],
    evaluates: [
      "تعداد لایه و وضعیت عرشه",
      "شیب، دره‌ها، دک و هندسه",
      "تهویه و شواهد رطوبت اتاق زیرشیروانی",
      "فهرست فلاشینگ و نفوذ",
      "دسترسی، حفاظت و دفع",
    ],
    causes: [
      "فرسایش عادی پایان عمر",
      "خزه و رطوبت مزمن",
      "آسیب باد انباشته",
      "تهویه ضعیف که پیری را تسریع می‌کند",
      "نصب‌های قبلی با جزئیات ضعیف",
    ],
    methodology: [
      "مستندسازی وضعیت و گزینه‌ها",
      "برنامه کندن، حفاظت و دفع",
      "تعمیر عرشه طبق کشف و مشخصات",
      "نصب زیرلایه، فلاشینگ و شینگل سطح اصلی سقف به ترتیب",
      "جزئیات شیروانی، دره، لبه و نفوذ به‌عنوان ساختار سقف کامل",
    ],
    materials: [
      "شینگل آسفالتی معماری",
      "زیرلایه و محافظ یخ/آب در صورت مناسب",
      "فلاشینگ دره، دیوار و نفوذ",
      "لوازم شیروانی و تهویه",
      "بست‌ها و اجزای لبه",
    ],
    bcClimate:
      "باران ساحلی و رشد خزه زیرلایه، جزئیات دره و تهویه را در سقف‌های شینگل لوئر مینلند به‌ویژه مهم می‌کند.",
    whenAppropriate:
      "وقتی فرسایش شینگل در کل سیستم است یا وصله مداوم خرابی‌های نزدیک‌مدت دیگر را حل نمی‌کند.",
    whenOtherService:
      "آسیب باد منزوی ممکن است هنوز تعمیر موضعی را مناسب کند اگر سطح اصلی سقف در غیر این صورت سالم باشد.",
    solutionCategories: [
      {
        title: "سیستم‌های لایه‌ای معماری",
        body: "شینگل مسکونی ابعادی که متناسب با شکل سقف، میزان باد و باران و سبک معماری خانه انتخاب می‌شود.",
      },
      {
        title: "زیرلایه و لایه‌های هدایت آب",
        body: "هماهنگی شینگل سطح اصلی سقف با دره‌ها، لبه‌ها و اتصالات.",
      },
      {
        title: "یکپارچه‌سازی مجدد فلاشینگ",
        body: "دیوار، دودکش، دریچه و لبه‌ها به‌عنوان بخشی از ساختار سقف جدید.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر موضعی وقتی آسیب محدود است و مصالح همسان تمیز یکپارچه می‌شوند.",
      replace: "تعویض منسجم است وقتی فرسایش گسترده است یا ساختار سقف وصله بادوام نمی‌پذیرد.",
    },
    scopeFactors: [
      "مساحت و شیب سقف",
      "تعداد لایه",
      "تعمیر عرشه",
      "پیچیدگی دره و دک",
      "انتخاب مصالح",
      "دسترسی و دفع",
    ],
    process: [
      "ارزیابی پوشش، شاخص‌های عرشه و تهویه",
      "شرح کار مکتوب با گزینه مصالح و جزئیات",
      "کندن با حفاظت ملک و دفع",
      "تعمیر عرشه، زیرلایه، فلاشینگ و نصب شینگل به ترتیب",
      "بازدید نهایی و بررسی تکمیل",
    ],
    publicPriceHint:
      "مساحت سقف، شیب، تعداد لایه، تعمیر عرشه، پیچیدگی دره و دک و انتخاب مصالح قیمت پروژه را تعیین می‌کند. برای شرح کار مکتوب ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعویض شینگل آسفالتی چیست؟",
        answer:
          "تعویض شینگل آسفالتی یک تعویض کامل سقف شیبدار است که پوشش شینگل موجود را برمی‌دارد، نیازهای زیرسازی و زیرلایه را برطرف می‌کند، سیستم شینگل جدید نصب می‌کند و فلاشینگ و نفوذها را دوباره یکپارچه می‌کند.",
      },
      {
        question: "تعویض شینگل آسفالتی چقدر طول می‌کشد؟",
        answer:
          "بیشتر تعویض و اجرای دوبارهٔ سقف تک‌خانواده بر حسب روز است نه هفته، اما مدت به اندازه سقف، شیب، پنجره آب‌وهوایی و تعمیر عرشه کشف‌شده در کندن بستگی دارد. شرح کار مکتوب پیش از شروع انتظارات را مشخص می‌کند.",
      },
      {
        question: "آیا می‌توان شینگل جدید روی قدیمی نصب کرد؟",
        answer:
          "روکش گاهی از نظر فنی ممکن است، اما کندن معمولاً ترجیح داده می‌شود: بازرسی عرشه، زیرلایه درست و یکپارچه‌سازی فلاشینگ را ممکن می‌کند و از انباشت وزن و پنهان کردن مشکلات جلوگیری می‌کند.",
      },
      {
        question: "اگر عرشه پوسیده در کندن پیدا شود چه؟",
        answer:
          "آسیب عرشه کشف‌شده مستند و پیش از نصب سیستم جدید تعمیر می‌شود. شرح کار روشن از پیش توضیح می‌دهد کار عرشه چگونه اندازه‌گیری و قیمت‌گذاری می‌شود تا یافته‌های میان پروژه شفاف باشند.",
      },
      {
        question: "کدام شینگل در آب‌وهمای مترو ونکوور بهتر دوام می‌آورد؟",
        answer:
          "شینگل لایه‌ای معماری انتخاب رایج مسکونی محلی است. در این اقلیم، کیفیت زیرلایه، جزئیات دره و لبه و تهویه اتاق زیرشیروانی حداقل به اندازه محصول شینگل بر عمر واقعی اثر می‌گذارد.",
      },
    ],
  },
  "roof-repair": {
    title: "تعمیر اضطراری نشت سقف",
    shortTitle: "نشت اضطراری",
    eyebrow: "ورود فعال آب",
    description:
      "تشخیص اولویت‌دار نشت: در همان روز در صورت امکان برنامه‌ریزی، با تعمیر موقت یا دائم موضعی وقتی آب از ساختار سقف وارد خانه مترو ونکوور می‌شود.",
    definition:
      "تعمیر اضطراری نشت سقف، بررسی متمرکز و تثبیت ورود فعال یا سریع‌الرشد آب از پوشش، فلاشینگ، نفوذ یا رابط مرتبط است. کار ممکن است شامل آب‌بندی موقت و تعمیر موضعی دائم در شرایط مناسب باشد.",
    metaTitle: "تعمیر اضطراری نشت سقف مترو ونکوور | رولین روفینگ",
    metaDescription:
      "تشخیص نشت سقف و تعمیر اضطراری موضعی برای خانه‌های مترو ونکوور، در همان روز در صورت امکان برنامه‌ریزی. ارزیابی مبتنی بر منبع و گام‌های بعدی روشن.",
    forWhom:
      "مالکانی با چکیدن فعال، لکه ناگهانی سقف هنگام باران، رطوبت اتاق زیرشیروانی پس از طوفان، یا آسیبی که پیش از برنامه‌ریزی کار گسترده‌تر به تشخیص و مهار ایمن نیاز دارد.",
    warningSigns: [
      "چکیدن فعال یا رطوبت پایدار حین یا پس از باران",
      "لکه جدید یا در حال گسترش روی سقف، دیوار یا اطراف چراغ",
      "عایق، عرشه یا قاب اتاق زیرشیروانی خیس",
      "رد آب در دره‌ها، دودکش، نورگیر یا دریچه لوله",
      "آسیب داخلی پس از هر رویداد آب‌وهوایی",
    ],
    evaluates: [
      "شواهد داخلی و مسیر محتمل آب",
      "وضعیت پوشش سقف اطراف منطقه آسیب‌دیده",
      "فلاشینگ، نفوذها، دره‌ها و رابط دیوار",
      "نیاز به حفاظت موقت در برابر تعمیر دائم",
      "آیا مصالح مجاور کار بادوام را پشتیبانی می‌کنند",
    ],
    causes: [
      "شکست فلاشینگ یا درزبند بوت لوله",
      "مصالح پوشش بلند شده با باد یا گم‌شده",
      "زهکشی مسدود با آوار که آب را متمرکز می‌کند",
      "شیروانی، لبه یا اتصال آسیب‌دیده در طوفان",
      "وصله‌های قبلی که منبع واقعی را حل نکرده‌اند",
    ],
    methodology: [
      "ثبت ایمن شواهد داخلی و بیرونی",
      "ردیابی محتمل‌ترین مسیر ورود، نه فقط محل لکه",
      "تثبیت با آب‌بندی موقت وقتی آب‌وهوا یا دسترسی نیاز دارد",
      "اجرای تعمیر موضعی سازگار وقتی ساختار سقف اطراف قابل سرویس است",
      "توضیح ریسک باقی‌مانده و اینکه آیا کار گسترده‌تر لازم است",
    ],
    materials: [
      "مصالح پوشش سازگار با سیستم موجود",
      "وصله زیرلایه یا غشا در صورت مناسب بودن",
      "فلز فلاشینگ و بوت نفوذ",
      "درزبند فقط به‌عنوان بخشی از جزئیات درست، نه تنها راه‌حل",
      "پارچه یا مصالح آب‌بندی موقت در صورت نیاز",
    ],
    bcClimate:
      "باران مکرر مترو ونکوور یعنی نقص‌های کوچک می‌توانند آسیب داخلی تکرارشونده ایجاد کنند. تشخیص به‌موقع مهم‌تر از وصله سطحی است که مسیر واقعی را حل نمی‌کند.",
    whenAppropriate:
      "وقتی ورود آب موضعی است، سقف اطراف قابل سرویس می‌ماند و تعمیر بادوام یا آب‌بندی موقت می‌تواند با ایمنی انجام شود.",
    whenOtherService:
      "خرابی گسترده پوشش، چند نشت مستقل، یا ساختار سقف در پایان عمر ممکن است برنامه تعمیر گسترده‌تر، فقط پوشش موقت پس از طوفان یا بحث تعویض کامل را لازم کند.",
    solutionCategories: [
      {
        title: "تشخیص منبع",
        body: "اتصال شواهد داخلی به جزئیات بیرونی که محتمل‌ترین محل ورود آب است، از جمله حرکت جانبی زیر پوشش.",
      },
      {
        title: "آب‌بندی موقت",
        body: "تثبیت بازشوها پس از آسیب طوفان یا وقتی مصالح دائم در همان بازدید تکمیل نمی‌شود.",
      },
      {
        title: "تعمیر موضعی دائم",
        body: "جایگزینی یا بازطراحی جزئیات خراب با مصالح سازگار با سیستم سقف اطراف.",
      },
    ],
    repairVsReplace: {
      repair:
        "تعمیر متمرکز معمولاً مناسب است وقتی یک منطقه خرابی شناسایی شود و مصالح اطراف برای پذیرش کار سازگار سالم بمانند.",
      replace:
        "تعویض وارد گفت‌وگو می‌شود وقتی بررسی فرسایش گسترده، چند خرابی مستقل یا ساختار سقفی که وصله بادوام نمی‌پذیرد نشان دهد.",
    },
    scopeFactors: [
      "دسترسی و ارتفاع سقف",
      "پنجره آب‌وهوایی",
      "میزان آسیب پنهان",
      "سازگاری مصالح",
      "تعداد نفوذهای درگیر",
      "نیاز به حفاظت موقت",
    ],
    process: [
      "تماس یا ارسال جزئیات و عکس آنچه می‌بینید",
      "اولویت‌بندی و برنامه‌ریزی: حضور در همان روز در صورت امکان",
      "تشخیص منبع روی سقف با مستندسازی داخلی و بیرونی",
      "آب‌بندی موقت یا تعمیر موضعی دائم بسته به شرایط",
      "خلاصه مکتوب یافته‌ها، کار انجام‌شده و گام‌های توصیه‌شده",
    ],
    publicPriceHint:
      "شرح کار فوری به دسترسی، آب‌وهوا حین کار، مسافت حرکت آب و نیاز به حفاظت موقت پیش از تعمیر دائم بستگی دارد. برای قیمت‌گذاری مخصوص پروژه ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر اضطراری نشت سقف چیست؟",
        answer:
          "تعمیر اضطراری نشت سقف، بررسی متمرکز و تثبیت ورود فعال یا سریع‌الرشد آب از پوشش، فلاشینگ، نفوذ یا رابط مرتبط است. کار ممکن است شامل آب‌بندی موقت و تعمیر موضعی دائم در شرایط مناسب باشد.",
      },
      {
        question: "آیا می‌توان نشت سقف را هنگام باران تعمیر کرد؟",
        answer:
          "گام ایمن اول در باران فعال معمولاً آب‌بندی موقت، پارچه یا مهر و موم ناحیه ورود، است چون بیشتر مصالح سقف برای چسبیدن درست به شرایط خشک نیاز دارند. تعمیر دائم سپس در پنجره آب‌وهوایی مناسب انجام می‌شود.",
      },
      {
        question: "چرا لکه سقفم دور از هر آسیب قابل‌مشاهده سقف است؟",
        answer:
          "آب اغلب از یک جزئیات وارد می‌شود و پیش از دیده شدن در داخل روی زیرلایه، قاب یا عرشه حرکت می‌کند. به همین دلیل تشخیص مسیر محتمل ورود را در کل سقف دنبال می‌کند نه فقط وصله بالای لکه.",
      },
      {
        question: "آیا تعمیر نشت سقف در همان روز در مترو ونکوور ارائه می‌شود؟",
        answer:
          "برای نشتی‌های فعال، رولین تلاش می‌کند در صورت مناسب بودن برنامه و روشنایی روز همان روز مراجعه کند؛ بااین‌حال زمان حضور تضمین‌شده نیست. فرستادن عکس و شرح دقیق آنچه می‌بینید، به ما کمک می‌کند فوریت درخواست را بهتر ارزیابی کنیم.",
      },
      {
        question: "آیا تعمیر نشت من موقت است یا دائم؟",
        answer:
          "به آب‌وهوا، دسترسی مصالح و یافته‌های بررسی بستگی دارد. وقتی تعمیر موضعی بادوام می‌تواند در بازدید اول با ایمنی انجام شود، انجام می‌شود. در غیر این صورت سقف با آب‌بندی موقت تثبیت و کار دائم مکتوب برنامه‌ریزی می‌شود.",
      },
      {
        question: "هزینه تعمیر اضطراری نشت سقف چقدر است؟",
        answer:
          "هزینه به ارتفاع و دسترسی سقف، میزان آسیب پنهان و نیاز احتمالی به محافظت موقت در کنار تعمیر اصلی بستگی دارد. رولین پس از ارزیابی، برآورد متناسب با همان پروژه را ارائه می‌دهد و بدون بازدید قیمت قطعی اعلام نمی‌کند.",
      },
    ],
  },
  "asphalt-shingle-roofing": {
    title: "سقف شینگل آسفالتی معماری",
    shortTitle: "شینگل آسفالتی",
    eyebrow: "سیستم شیبدار",
    description:
      "دسته سقفی عملی و با توجه به طراحی برای بسیاری از خانه‌های مترو ونکوور با سیستم سقف شیبدار مناسب.",
    forWhom:
      "شینگل آسفالتی معماری برای بسیاری از سقف‌های مسکونی شیبدار مناسب است که مالک سیستمی آشنا، انتخاب‌های زیبایی گسترده و امکان سرویس ساده در آینده می‌خواهد.",
    warningSigns: [
      "از دست رفتن دانه یا نمایان شدن لایه زیرین",
      "قوز، ترک یا جابه‌جایی شینگل",
      "آسیب اطراف دره، لبه یا نفوذ",
      "رشد خزه روی سطح سقف",
      "تعمیر مکرر در چند سطح",
    ],
    evaluates: [
      "شیب و هندسه سقف",
      "وضعیت عرشه پس از بررسی مناسب",
      "زیرلایه، پیش‌آمدگی و دسترسی به دره",
      "تهویه و مسیرهای هوا",
      "فلاشینگ و زهکشی",
    ],
    solutionCategories: [
      {
        title: "شینگل لایه‌ای معماری",
        body: "پروفیل ابعادی رایج برای سقف‌های شیبدار مسکونی که بر اساس ساختار سقف و الزامات طراحی انتخاب می‌شود.",
      },
      {
        title: "زیرلایه و جزئیات هدایت آب",
        body: "شینگل قابل‌مشاهده یک لایه از ساختار سقف هماهنگ شامل روکش، دره، لبه و اتصال است.",
      },
      {
        title: "یکپارچه‌سازی فلاشینگ",
        body: "اتصال دیوار، دودکش، دریچه لوله و عناصر سقف به ترتیب عمدی با سطح اصلی شینگل نیاز دارد.",
      },
    ],
    repairVsReplace: {
      repair:
        "تعمیر موضعی عملی است اگر شینگل همسان موجود باشد و شینگل سازگار بدون آسیب به سطح اصلی سقف شکننده اطراف جای گیرد.",
      replace:
        "اگر شینگل در چند سطح به‌طور گسترده شکننده، فرسوده یا آسیب‌دیده باشد، تعویض مناسب‌تر است.",
    },
    scopeFactors: [
      "شیب و مساحت کل",
      "تعداد دره، دریچه سقفی و نفوذ",
      "لایه‌های موجود و تعمیر عرشه",
      "پروفیل شینگل و لوازم",
      "تهویه و کار فلاشینگ",
      "دسترسی و حفاظت سایت",
    ],
    faqs: [
      {
        question: "آیا شینگل معماری برای همه سقف‌های شیبدار مناسب است؟",
        answer:
          "خیر. شیب، زیرسازی، میزان قرارگیری در معرض باد و باران، شکل و الزامات محلی باید در نظر گرفته شود. سقف پیش از انتخاب محصول به‌عنوان ساختار سقف ارزیابی می‌شود.",
      },
      {
        question: "آیا رنگ بر نتیجه معماری اثر دارد؟",
        answer:
          "بله. رنگ سقف با نما، تریم، حجم و دید از خیابان تعامل دارد. نمونه‌ها باید کنار ساختمان واقعی بررسی شوند، نه فقط روی صفحه.",
      },
    ],
  },
  "metal-roofing": {
    title: "تعویض سقف فلزی",
    shortTitle: "تعویض فلزی",
    eyebrow: "تعویض و اجرای دوبارهٔ سقف فلزی ممتاز",
    description:
      "تعویض و اجرای دوبارهٔ سقف فلزی درز ایستاده یا سایر سیستم‌های فلزی ممتاز برای معماری شیبدار مناسب.",
    definition:
      "تعویض سقف فلزی سیستم سقف فلزی جدید، اغلب درز ایستاده، پس از ارزیابی هندسه، زیرسازی، زیرلایه و قصد معماری نصب می‌کند.",
    metaTitle: "تعویض سقف فلزی مترو ونکوور | رولین روفینگ",
    metaDescription:
      "تعویض سقف فلزی درز ایستاده و ممتاز برای خانه‌های مترو ونکوور. انتخاب سیستم مبتنی بر هندسه و یکپارچه‌سازی کامل فلاشینگ.",
    forWhom:
      "مالکانی که سیستم فلزی ممتاز را برای هندسه، معماری و برنامه‌ریزی بلندمدت مناسب انتخاب می‌کنند.",
    warningSigns: [
      "پوشش موجود در پایان عمر با انتخاب فلز به‌عنوان سیستم جدید",
      "معماری مناسب درز ایستاده",
      "تمایل به پروفیل پوشش ممتاز",
      "هندسه پیچیده که در پنل فلزی بهتر بیان می‌شود",
    ],
    evaluates: [
      "انتخاب پروفیل",
      "زیرلایه و زیرسازی",
      "رویکرد کنترل تراکم",
      "فلاشینگ و نفوذها",
      "انتظارات آکوستیک و طراحی",
    ],
    causes: [
      "ارتقای برنامه‌ریزی‌شده سیستم",
      "پایان عمر سقف موجود",
      "بازسازی طراحی",
      "اهداف عملکردی همسو با ساختار سقف فلزی",
    ],
    methodology: [
      "تأیید مناسب بودن هندسه",
      "مشخص کردن سیستم پنل و زیرلایه",
      "آماده‌سازی زیرسازی",
      "نصب پنل و فلاشینگ به‌عنوان سیستم کامل",
      "جزئیات عمدی شیروانی، لبه و نفوذ",
    ],
    materials: [
      "پنل درز ایستاده یا فلزی مشخص‌شده",
      "سیستم‌های زیرلایه",
      "گیره/بست طبق سیستم",
      "فلاشینگ و درزبند",
    ],
    bcClimate:
      "معماری ساحل غربی اغلب با فلز درز ایستاده همخوان است، اما زیرلایه، تهویه و جزئیات همچنان عملکرد واقعی در باران را تعیین می‌کنند.",
    whenAppropriate: "وقتی فلز سیستم انتخاب‌شده است و شرح کار پروژه تعویض کامل سقف است.",
    whenOtherService:
      "تعمیر جزئی ممکن است هنوز مناسب باشد اگر سقف فلزی موجود در غیر این صورت سالم است.",
    solutionCategories: [
      {
        title: "سیستم‌های درز ایستاده",
        body: "زیبایی‌شناسی پنل ممتاز با تثبیت پنهان در صورت مشخص.",
      },
      {
        title: "زیرلایه و آماده‌سازی زیرسازی",
        body: "ساخت پایه هدایت آب زیر فلز.",
      },
      {
        title: "جزئیات معماری",
        body: "لبه، دره و دیوار برای عملکرد و ظاهر.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر فلز موجود وقتی پنل‌ها سالم می‌مانند.",
      replace: "تعویض وقتی سیستم فلزی جدید هدف پروژه است یا پوشش قدیمی تمام شده.",
    },
    scopeFactors: ["مساحت و هندسه سقف", "پروفیل پنل", "انتخاب زیرلایه", "دسترسی", "پیچیدگی نفوذ"],
    process: [
      "تأیید مناسب بودن هندسه و سیستم",
      "مشخص کردن پروفیل پنل، زیرلایه و جزئیات",
      "شرح کار مکتوب با تصمیم‌های طراحی مستند",
      "آماده‌سازی زیرسازی و نصب پنل",
      "بررسی جزئیات و بازدید تکمیل",
    ],
    publicPriceHint:
      "قیمت تعویض و اجرای دوبارهٔ سقف فلزی به هندسه سقف، پروفیل پنل، استراتژی زیرلایه و پیچیدگی نفوذ بستگی دارد. برای شرح کار مکتوب ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعویض سقف فلزی چیست؟",
        answer:
          "تعویض سقف فلزی سیستم سقف فلزی جدید، اغلب درز ایستاده، پس از ارزیابی هندسه، زیرسازی، زیرلایه و قصد معماری نصب می‌کند.",
      },
      {
        question: "آیا سقف فلزی در باران پرسر و صداست؟",
        answer:
          "در تعویض و اجرای دوبارهٔ سقف مسکونی روی عرشه محکم با زیرلایه و عایق زیرین، صدای باران کمتر از انتظار بیشتر مالکان است: ارتباط بلند با پنل روی قاب باز است. انتظارات آکوستیک در انتخاب سیستم بحث می‌شود.",
      },
      {
        question: "درز ایستاده در برابر فلز پیچ‌دار: تفاوت چیست؟",
        answer:
          "درز ایستاده تثبیت را روی گیره زیر درزهای برجسته پنهان می‌کند و حرکت پنل را می‌پذیرد. پنل پیچ‌دار ارزان‌تر است اما نگهداری بست بلندمدت دارد. برای کار مسکونی ممتاز، درز ایستاده معمولاً توصیه می‌شود.",
      },
      {
        question: "آیا می‌توان فلز را روی شینگل موجود نصب کرد؟",
        answer:
          "گاهی از نظر سازه ممکن است، اما کندن معمولاً ترجیح داده می‌شود تا عرشه بازرسی و سیستم زیرلایه و فلاشینگ درست ساخته شود. پنهان کردن پوشش قدیمی تشخیص بعدی را دشوار می‌کند.",
      },
      {
        question: "آیا سقف فلزی روی اشکال پیچیده کار می‌کند؟",
        answer:
          "بله، اما پیچیدگی جایی پروژه‌های فلزی برد یا باخت می‌شود: شیروانی، دره، دک و جزئیات بن‌بست به مهارت فلزکاری و هزینه بیشتر نیاز دارند. بررسی هندسه اولین گام تصمیم است.",
      },
    ],
  },
  "flat-roofing": {
    title: "تعویض سقف مسطح: SBS / TPO / PVC",
    shortTitle: "تعویض مسطح",
    eyebrow: "تعویض و اجرای دوبارهٔ سقف کم‌شیب",
    description:
      "تعویض کامل سقف مسکونی یا تجاری کم‌شیب با SBS، TPO، PVC یا سایر سیستم‌های غشای مشخص‌شده.",
    definition:
      "تعویض سقف مسطح ساختار سقف کم‌شیب را برمی‌دارد یا روکش می‌کند و سیستم غشای جدید متناسب با زهکشی، زیرسازی و نیازهای پروژه نصب می‌کند.",
    metaTitle: "تعویض سقف مسطح SBS، TPO و PVC بریتیش کلمبیا | رولین روفینگ",
    metaDescription:
      "تعویض کامل سقف مسطح و کم‌شیب در مترو ونکوور با SBS، TPO، PVC یا سایر غشاهای مشخص‌شده پس از ارزیابی.",
    forWhom:
      "مالکانی با خرابی مکرر غشا، عایق خیس، سیستم منسوخ یا بازسازی نمای بیرونی هماهنگ روی سقف کم‌شیب.",
    warningSigns: [
      "چند خرابی غشا",
      "عایق خیس",
      "سیستم منسوخ یا غیرقابل سرویس",
      "بازسازی نمای بیرونی هماهنگ",
      "تجمع آب مزمن با فرسایش در کل سیستم",
    ],
    evaluates: [
      "گزینه‌های نوع غشا",
      "نیازهای عایق و زیرسازی",
      "زهکش و سرریز",
      "دیوار حائل و نفوذها",
      "دسترسی و حفاظت",
    ],
    causes: [
      "پایان عمر غشا",
      "اشباع عایق",
      "محدودیت طراحی زهکشی",
      "پیری فرابنفش و حرارتی",
      "تراکم نفوذ با خرابی جزئیات انباشته",
    ],
    methodology: [
      "ارزیابی ساختار سقف از جمله اجزای خیس در صورت نیاز بررسی",
      "انتخاب استراتژی غشا برای پروژه",
      "برداشتن یا روکش طبق مشخصات",
      "نصب عایق و لایه غشا به ترتیب",
      "جزئیات زهکش، دیوار حائل و نفوذ به‌عنوان سیستم",
    ],
    materials: [
      "غشاهای SBS، TPO، PVC یا سایر مشخص‌شده",
      "عایق و تخته روکش طبق شرح کار",
      "لوازم زهکش و فلاشینگ",
      "چسب، بست یا فرآیند حرارتی متناسب با سیستم",
    ],
    bcClimate:
      "سقف‌های کم‌شیب در مترو ونکوور بارانی به طراحی زهکشی و جزئیات غشای قوی بیش از برچسب بازاریابی وابسته‌اند.",
    whenAppropriate:
      "وقتی ساختار سقف کم‌شیب تمام شده یا به‌عنوان بخشی از پروژه گسترده‌تر تازه می‌شود.",
    whenOtherService:
      "تعمیر موضعی غشا ممکن است هنوز روی سیستم در غیر این صورت سالم قابل اجرا باشد.",
    solutionCategories: [
      {
        title: "انتخاب سیستم غشا",
        body: "انتخاب SBS، TPO، PVC یا ساختار سقف دیگر برای شرایط پروژه.",
      },
      {
        title: "تعویض ساختار سقف",
        body: "برطرف کردن غشا و لایه‌های مرتبط طبق شرح کار تأییدشده.",
      },
      {
        title: "زهکشی و جزئیات",
        body: "زهکش، دیوار حائل و نفوذ به‌عنوان کار اصلی ضدآب.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر اگر سطح اصلی غشا هنوز قابل سرویس است.",
      replace: "تعویض کامل کم‌شیب وقتی سیستم تمام شده.",
    },
    scopeFactors: ["مساحت و هندسه", "نوع غشا", "نیاز عایق", "تراکم نفوذ", "دسترسی و دفع"],
    process: [
      "ارزیابی ساختار سقف، از جمله بررسی رطوبت در صورت لزوم",
      "انتخاب سیستم غشا و شرح کار مکتوب",
      "برداشتن یا روکش ساختار سقف موجود طبق مشخصات",
      "نصب عایق و غشا به ترتیب",
      "بررسی جزئیات زهکش، دیوار حائل و نفوذ",
    ],
    publicPriceHint:
      "قیمت تعویض کم‌شیب به مساحت، انتخاب غشا، نیاز عایق، کار زهکشی و تراکم نفوذ بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعویض سقف مسطح با SBS، TPO یا PVC چیست؟",
        answer:
          "تعویض سقف مسطح ساختار سقف کم‌شیب را برمی‌دارد یا روکش می‌کند و سیستم غشای جدید متناسب با زهکشی، زیرسازی و نیازهای پروژه نصب می‌کند.",
      },
      {
        question: "SBS، TPO یا PVC: کدام غشای مسطح بهتر است؟",
        answer:
          "هیچ‌کدام جهانی بهتر نیست. SBS دو لایه پایه رایج منطقه با افزونگی قوی است؛ TPO و PVC تک‌لایه با درز جوش حرارتی، وزن کمتر و گزینه‌های بازتابنده‌اند و PVC مقاومت شیمیایی قوی دارد. زیرسازی، زهکشی، استفاده از پشت‌بام و بودجه تصمیم می‌گیرند.",
      },
      {
        question: "کندن کنیم یا روی سقف مسطح موجود روکش بگذاریم؟",
        answer:
          "روکش می‌تواند هزینه و دفع را کاهش دهد وقتی ساختار سقف موجود خشک و سالم است، اما عایق خیس یا چند لایه موجود کندن را مسیر صادقانه می‌کند. بررسی رطوبت پیش از تصمیم از پنهان کردن مشکلات جلوگیری می‌کند.",
      },
      {
        question: "تعویض سقف مسطح چقدر مزاحم است؟",
        answer:
          "انتظار صدا، بو در کار مشعل یا چسب، بارگیری مرحله‌ای مصالح و مسیرهای محافظت‌شده را داشته باشید. معمولاً ساکنان می‌توانند بمانند و شرح کار مکتوب توالی، ساعات کار و اقدامات حفاظت را از پیش مشخص می‌کند.",
      },
      {
        question: "آیا می‌توان زهکشی را در تعویض سقف مسطح بهبود داد؟",
        answer:
          "تعویض لحظه درست برای اصلاح تجمع آب مزمن است: عایق شیبدار، زهکش اضافه یا جابجا شده و کار سرریز می‌تواند در ساختار سقف جدید طراحی شود نه بعداً.",
      },
    ],
  },
  "roof-inspection-maintenance": {
    title: "بررسی سلامت سقف و نگهداری",
    shortTitle: "بررسی سلامت",
    eyebrow: "ابتدا وضعیت",
    description:
      "بازرسی، عکس، کار جزئی درزبند یا نگهداری و گزارش وضعیت سقف برای برنامه‌ریزی آگاهانه.",
    definition:
      "بررسی سلامت سقف ارزیابی بصری ساختاریافته پوشش، فلاشینگ، زهکشی و اجزای مرتبط قابل دسترس است، معمولاً همراه مستندسازی عکس و موارد نگهداری محدود توافق‌شده از پیش.",
    metaTitle: "بررسی سلامت و نگهداری سقف ونکوور | رولین روفینگ",
    metaDescription:
      "بررسی سلامت سقف، عکس و اولویت‌های نگهداری برای مالکان مترو ونکوور. مستندسازی وضعیت روشن پیش از تصمیم تعمیر یا تعویض.",
    forWhom:
      "مالکانی که برای خرید یا فروش، بازسازی، علائم اولیه یا خط پایه مستند پیش از فصل بارانی آماده می‌شوند.",
    warningSigns: [
      "عدم قطعیت درباره سن یا وضعیت باقی‌مانده سقف",
      "لکه‌های جزئی بدون منبع روشن",
      "آوار فصلی و تجمع خزه",
      "درزبند پیر در نفوذها",
      "نیاز به خط پایه برنامه‌ریزی مکتوب",
    ],
    evaluates: [
      "وضعیت پوشش به تفکیک سطح",
      "فلاشینگ و نفوذها",
      "زهکشی و رابط ناودان",
      "اجزای تهویه قابل‌مشاهده",
      "اولویت‌بندی نگهداری در برابر تعمیر",
    ],
    causes: [
      "پیری عادی بدون مستندسازی",
      "نگهداری به‌تعویق افتاده",
      "شرایط آب‌وهوایی همان ملک",
      "کار قبلی با کیفیت نامعلوم",
      "نیاز تصمیم خریدار یا بازسازی",
    ],
    methodology: [
      "بررسی نگرانی‌ها و سابقه قابل دسترس",
      "بازرسی سیستماتیک نواحی بیرونی سقف",
      "عکس از شرایط نماینده",
      "تکمیل فقط نگهداری جزئی توافق‌شده",
      "ارائه خلاصه وضعیت روشن و گزینه‌ها",
    ],
    materials: [
      "مستندسازی عکس",
      "درزبند یا بست جزئی در صورت گنجاندن",
      "پاکسازی آوار محدود در صورت درج در شرح کار",
      "یادداشت وضعیت مکتوب",
    ],
    bcClimate:
      "باران فصلی بررسی سلامت پیش از زمستان و پس از طوفان را برای یافتن زهکشی مسدود، مصالح بلند شده و خرابی اولیه فلاشینگ مفید می‌کند.",
    whenAppropriate: "وقتی قبل از تعهد به تعمیر، تعویض یا بررسی بیشتر به وضوح نیاز دارید.",
    whenOtherService: "نشت فعال بزرگ یا بازشوهای طوفانی باید به مسیر اضطراری یا تعمیر برود.",
    solutionCategories: [
      {
        title: "بازرسی مستند",
        body: "بررسی ساختاریافته آنچه روی ساختار سقف قابل‌مشاهده و دسترس است.",
      },
      {
        title: "نگهداری جزئی",
        body: "کار درزبند یا آوار محدود فقط وقتی گنجانده و مناسب باشد.",
      },
      {
        title: "خلاصه برنامه‌ریزی",
        body: "جداسازی نگرانی‌های فوری از موارد پایش میان‌مدت.",
      },
    ],
    repairVsReplace: {
      repair: "بسیاری از یافته‌ها نگهداری یا تعمیر موضعی‌اند نه محرک تعویض کامل.",
      replace: "تعویض فقط وقتی بحث می‌شود که وضعیت مشاهده‌شده مسیر گسترده‌تر را پشتیبانی کند.",
    },
    scopeFactors: [
      "اندازه و پیچیدگی سقف",
      "دسترسی",
      "عمق مستندسازی عکس",
      "موارد نگهداری گنجانده‌شده",
      "تعداد سیستم‌های سقف در ملک",
    ],
    process: [
      "رزرو بررسی سلامت و توضیح نگرانی‌ها",
      "بازرسی بیرونی سیستماتیک با مستندسازی عکس",
      "تکمیل موارد نگهداری جزئی توافق‌شده در همان بازدید",
      "خلاصه وضعیت مکتوب با اولویت‌های رتبه‌بندی‌شده",
      "گفت‌وگوی پیگیری درباره تعمیرات توصیه‌شده",
    ],
    publicPriceHint:
      "شرح کار بررسی سلامت به اندازه و پیچیدگی سقف، دسترسی و میزان مستندسازی و نگهداری جزئی گنجانده‌شده بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "بررسی سلامت سقف و نگهداری چیست؟",
        answer:
          "بررسی سلامت سقف ارزیابی بصری ساختاریافته پوشش، فلاشینگ، زهکشی و اجزای مرتبط قابل دسترس است، معمولاً همراه مستندسازی عکس و موارد نگهداری محدود توافق‌شده از پیش.",
      },
      {
        question: "چند وقت یک‌بار باید سقف بازرسی شود؟",
        answer:
          "ریتم مفید محلی بررسی پیش از باران پاییز، به‌علاوه پس از هر طوفان باد شدید است. خانه‌های زیر پوشش درختی سنگین یا سقف در نیمه دوم عمر مورد انتظار از توجه بیشتر بهره می‌برند.",
      },
      {
        question: "پس از بررسی سلامت سقف چه دریافت می‌کنم؟",
        answer:
          "خلاصه مستند با عکس از وضعیت پوشش، فلاشینگ، زهکشی و تهویه، تفکیک‌شده به موارد نیازمند تعمیر فوری، موارد پایش و فرسایش عادی. سند برنامه‌ریزی است که با سرعت خودتان اقدام کنید.",
      },
      {
        question: "آیا بررسی سقف قبل از خرید یا فروش خانه ارزش دارد؟",
        answer:
          "بله، سقف یکی از پرهزینه‌ترین اجزای ساختمان برای نوسازی است و خط پایه وضعیت مستند حدس مذاکره را کم می‌کند. توجه کنید این بررسی وضعیت سقف است، نه بازرسی رسمی پیش از خرید.",
      },
      {
        question: "آیا مشکلات یافت‌شده در بازرسی را تعمیر می‌کنید؟",
        answer:
          "موارد نگهداری کوچک توافق‌شده، مانند تازه‌سازی جزئی درزبند در نفوذها، می‌تواند در همان بازدید انجام شود. یافته‌های بزرگ‌تر مستند و جداگانه شرح کار جداگانه برایشان تعیین می‌شود تا با اطلاعات روشن تصمیم بگیرید نه تحت فشار لحظه‌ای.",
      },
    ],
  },
  "flashing-roof-penetration-repair": {
    title: "تعمیر فلاشینگ / نفوذ سقف",
    shortTitle: "فلاشینگ و نفوذ",
    eyebrow: "اتصالات و رابط‌ها",
    description:
      "تعمیر فلاشینگ دودکش، دیوار، دریچه، بوت لوله و سایر نفوذهای سقف که آب معمولاً از آن‌ها وارد سقف‌های مترو ونکوور می‌شود.",
    definition:
      "تعمیر فلاشینگ و نفوذ سقف رابط‌های هدایت آب را اصلاح می‌کند جایی که پوشش سقف به دیوار، دودکش، دریچه، لوله و نفوذهای دیگر می‌رسد. این جزئیات وقتی فلزکاری، بوت یا درزبند شکست بخورند منبع رایج نشت‌اند.",
    metaTitle: "تعمیر فلاشینگ و نفوذ سقف ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر فلاشینگ دودکش، دیوار، دریچه و بوت لوله برای سقف‌های مترو ونکوور. ارزیابی مبتنی بر منبع و بازطراحی سازگار نفوذها.",
    forWhom:
      "مالکانی با نشت نزدیک دودکش، دیوار جانبی، لوله فاضلاب، دریچه مکانیکی یا سایر بازشوهای سقف، یا فلاشینگ پیر که دیگر آب را درست هدایت نمی‌کند.",
    warningSigns: [
      "لکه نزدیک محفظه دودکش یا دیوار بیرونی",
      "درزبند ترک‌خورده یا سخت در نفوذها",
      "فلاشینگ پله‌ای یا فلاشینگ رویی بلند، زنگ‌زده یا جدا",
      "بوت لاستیکی پیر شده",
      "ورود آب در باران بادی در اتصال دیوار به سقف",
    ],
    evaluates: [
      "هندسه و وضعیت فلاشینگ",
      "سازگاری با پوشش سقف اطراف",
      "یکپارچگی فلاشینگ فلاشینگ رویی و رابط دیوار",
      "وضعیت بوت و یقه نفوذ",
      "آیا درزبند شکست فلزی را پنهان می‌کند",
    ],
    causes: [
      "حرکت حرارتی که درزها را باز می‌کند",
      "پیری فرابنفش و آب‌وهوا در بوت و درزبند",
      "یکپارچه‌سازی نادرست فلاشینگ پله‌ای یا فلاشینگ رویی",
      "خوردگی یا آسیب مکانیکی فلز",
      "تعویض پوشش بدون بازطراحی صحیح نفوذها",
    ],
    methodology: [
      "شناسایی هر نفوذ و رابط دیوار در منطقه خرابی",
      "باز کردن فقط به اندازه تأیید مسیر آب",
      "جایگزینی بوت، یقه یا فلزکاری خراب با جزئیات سازگار",
      "یکپارچه‌سازی مجدد فلاشینگ با پوشش سطح اصلی سقف",
      "اجتناب از اتکای سطحی به درزگیر به‌عنوان استراتژی ضدآب دائم",
    ],
    materials: [
      "فلاشینگ پله‌ای، فلاشینگ رویی و پیش‌آمدگی",
      "بوت لوله و یقه دریچه",
      "زیرلایه یا غشای یخ/آب سازگار در رابط‌های بحرانی",
      "فلز مناسب شرایط ساحلی و مصالح مجاور",
      "درزبند سازگار سازنده به‌عنوان محافظ ثانویه نه اولیه",
    ],
    bcClimate:
      "باران بادی و فصل مرطوب طولانی فلاشینگ و بوت الاستومری را تحت فشار می‌گذارد. جزئیاتی که در اقلیم خشک‌تر کار می‌کنند در ساحل بریتیش کلمبیا بدون یکپارچه‌سازی فلزی درست زودتر شکست می‌خورند.",
    whenAppropriate:
      "وقتی پوشش سطح اصلی سقف هنوز قابل سرویس است و نشت از خرابی مجزای فلاشینگ یا نفوذ ناشی می‌شود.",
    whenOtherService:
      "اگر پوشش اطراف به‌طور گسترده فرسوده است یا دودکش، نورگیر یا ساختار سقف کم‌شیب شرح کار متفاوتی می‌خواهد، خدمات تخصصی‌تر مناسب‌ترند.",
    solutionCategories: [
      { title: "جایگزینی بوت نفوذ", body: "جایگزینی بوت لوله و دریچه پیر پیش از ترک و ورود آب." },
      {
        title: "تعمیر فلاشینگ دیوار و پله",
        body: "بازگرداندن رابط دیوار به سقف وقتی فلاشینگ پله‌ای و فلاشینگ رویی دیگر آب را هدایت نمی‌کند.",
      },
      {
        title: "کار دودکش و رابط پیچیده",
        body: "رسیدگی به ساختار سقفهای چندسطحی فلاشینگ که نیاز به توالی دقیق با پوشش دارند.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر فلاشینگ مناسب است وقتی سطح اصلی سقف سالم است و خرابی در رابط متمرکز است.",
      replace:
        "تعویض سطح اصلی سقف ممکن است لازم باشد اگر فلاشینگ‌ها باید کاملاً در پوششی که در پایان عمر است یکپارچه شوند.",
    },
    scopeFactors: [
      "تعداد و نوع نفوذها",
      "رابط روکش دیوار",
      "سازگاری فلز",
      "دسترسی اطراف دودکش و دک",
      "وضعیت پوشش موجود",
      "نیاز به باز کردن ساختار سقف مجاور",
    ],
    process: [
      "محل نشت و جزئیات قابل‌مشاهده اطراف آن را توضیح دهید",
      "بازرسی سطح سقف فلاشینگ و نفوذها در منطقه خرابی",
      "شرح کار تعمیر مکتوب با شناسایی هر جزئیات اصلاح‌شونده",
      "بازطراحی با فلز، بوت و غشای سازگار",
      "بازبینی نهایی رابط‌های اصلاح‌شده و پوشش اطراف",
    ],
    publicPriceHint:
      "قیمت‌گذاری به تعداد و نوع نفوذها، پیچیدگی رابط دیوار و میزان باز و یکپارچه‌سازی مجدد پوشش اطراف بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر فلاشینگ / نفوذ سقف چیست؟",
        answer:
          "تعمیر فلاشینگ و نفوذ سقف رابط‌های هدایت آب را اصلاح می‌کند جایی که پوشش به دیوار، دودکش، دریچه، لوله و نفوذهای دیگر می‌رسد.",
      },
      {
        question: "چگونه بفهمم نشت سقفم مشکل فلاشینگ است؟",
        answer:
          "نشت‌های نزدیک دودکش، دیوار جانبی، نورگیر، لوله فاضلاب یا دریچه، به‌ویژه در باران بادی، معمولاً مربوط به فلاشینگ‌اند. لکه‌هایی که با نفوذ بالایی هم‌راستا هستند نشانه قوی‌اند، اما تأیید به بازرسی سطح سقف نیاز دارد.",
      },
      {
        question: "آیا می‌توان فلاشینگ را بدون تعویض سقف تعمیر کرد؟",
        answer:
          "معمولاً بله، وقتی پوشش اطراف قابل سرویس است. فلاشینگ، بوت و یقه‌های مجزا قابل جایگزینی و بافت مجدد در سقف موجود‌اند. اگر سطح اصلی سقف در پایان عمر است، بازطراحی بهتر با برنامه تعویض ترکیب شود.",
      },
      {
        question: "چرا درزگیر روی فلاشینگ نشت را متوقف نکرد؟",
        answer:
          "درزبند خیلی سریع‌تر از فلز پیر می‌شود و نمی‌تواند هندسه نادرست فلاشینگ را جبران کند. درزگیر سطحی اغلب شکست را پنهان می‌کند در حالی که آب همچنان وارد می‌شود. تعمیر بادوام جزئیات فلزی را بازمی‌گرداند؛ درزبند نقش ثانویه دارد.",
      },
      {
        question: "بوت لوله و فلاشینگ در ساحل بریتیش کلمبیا چقدر دوام می‌آورند؟",
        answer:
          "عمر مفید به نوع مصالح، میزان باد و باران و کیفیت نصب بستگی دارد؛ بنابراین نمی‌توان یک عدد را برای همهٔ شرایط اعلام کرد. قطعات لاستیکی معمولاً زودتر از فلز فرسوده می‌شوند، زیرا تابش فرابنفش و تغییرات دما آن‌ها را خشک و سخت می‌کند و باران‌های همراه با باد در مناطق ساحلی نیز ایرادهای اجرایی را زودتر آشکار می‌سازد.",
      },
    ],
  },
  "shingle-spot-repair": {
    title: "تعمیر موضعی شینگل",
    shortTitle: "تعمیر موضعی",
    eyebrow: "تعمیر موضعی پوشش",
    description:
      "تعمیر شینگل آسفالتی گم‌شده، آسیب باد، ترک‌خورده یا آسیب‌دیده در نواحی محدود وقتی سطح اصلی سقف اطراف قابل سرویس است.",
    definition:
      "تعمیر موضعی شینگل بخش محدودی از شینگل آسفالتی آسیب‌دیده را بدون تعویض کامل سقف جایگزین یا دوباره می‌نشاند وقتی تطابق و شرایط اطراف اجازه دهد.",
    metaTitle: "تعمیر موضعی شینگل مترو ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر شینگل آسفالتی گم‌شده، ترک‌خورده یا آسیب باد در خانه‌های مترو ونکوور وقتی سطح اصلی سقف اطراف قابل سرویس است.",
    forWhom: "مالکانی با آسیب شینگل منزوی روی سقف شیبدار که در غیر این صورت سالم است.",
    warningSigns: [
      "شینگل گم‌شده پس از باد",
      "لبه ترک‌خورده",
      "بست یا زیرلایه نمایان در ناحیه محدود",
      "تپه دانه از یک ناحیه",
      "وصله‌های قبلی بلند شده",
    ],
    evaluates: [
      "قابلیت تطابق شینگل",
      "انعطاف سطح اصلی سقف اطراف",
      "وضعیت زیرلایه محلی",
      "میزان قرارگیری در معرض باد و باران سطح آسیب‌دیده",
      "آیا آسیب واقعاً منزوی است",
    ],
    causes: ["بلند شدن با باد", "آسیب ضربه", "ترک حرارتی", "آسیب تردد", "شکست نوار درزبند با سن"],
    methodology: [
      "تأیید تطابق",
      "حذف دقیق شینگل آسیب‌دیده",
      "جایگزینی زیرلایه محلی در صورت نیاز",
      "نصب شینگل همسان با بست صحیح",
      "درزبند فقط طبق جزئیات",
    ],
    materials: [
      "شینگل آسفالتی همسان",
      "زیرلایه محلی",
      "بست سازگار",
      "درزبند جزئیات فقط در صورت مشخص",
    ],
    bcClimate:
      "رویدادهای باد ساحلی و زمستان‌های مرطوب تعمیر موضعی به‌موقع را قبل از رسیدن آب به عرشه مهم می‌کند.",
    whenAppropriate: "وقتی آسیب محدود است و سطح اصلی شینگل سازگار می‌پذیرد.",
    whenOtherService: "شکنندگی گسترده یا خرابی چند سطحی به سمت تعویض آسفالتی کامل می‌رود.",
    solutionCategories: [
      { title: "جایگزینی موضعی شینگل", body: "بازگرداندن ناحیه آسیب محدود با مصالح سازگار." },
      {
        title: "لمس لبه و شیروانی",
        body: "اصلاح خرابی‌های مجزا در لبه‌های پرتنش وقتی هنوز منزوی‌اند.",
      },
      { title: "بررسی مسیر نشت", body: "تأیید اینکه آسیب موضعی شکست فلاشینگ را پنهان نمی‌کند." },
    ],
    repairVsReplace: {
      repair: "تعمیر موضعی وقتی سطح اصلی سقف انعطاف‌پذیر و قابل تطابق است.",
      replace: "تعویض کامل وقتی فرسایش سیستماتیک است.",
    },
    scopeFactors: ["مساحت آسیب", "شیب و دسترسی", "تطابق مصالح", "نیاز زیرلایه", "پنجره آب‌وهوایی"],
    process: [
      "ارسال عکس ناحیه آسیب در صورت دید امن",
      "تأیید روی سقف که آسیب واقعاً محدود است",
      "بررسی تطابق پروفیل و رنگ شینگل",
      "حذف و جایگزینی دقیق شینگل‌های آسیب‌دیده",
      "بازبینی تعمیر و سطح اصلی سقف اطراف",
    ],
    publicPriceHint:
      "قیمت تعمیر موضعی به مساحت آسیب، شیب، دسترسی و میزان تطابق شینگل جایگزین بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر موضعی شینگل چیست؟",
        answer:
          "جایگزینی یا نشاندن مجدد بخش محدودی از شینگل آسفالتی آسیب‌دیده بدون تعویض کامل سقف وقتی تطابق و شرایط اطراف اجازه دهد.",
      },
      {
        question: "چند شینگل با باد رفت: فوری است؟",
        answer:
          "سریع رسیدگی کنید. شینگل گم‌شده زیرلایه را نمایان می‌کند که برای میزان قرارگیری در معرض باد و باران بلندمدت طراحی نشده و باران بعدی می‌تواند به عرشه برسد.",
      },
      {
        question: "آیا شینگل جایگزین با سقف موجود تطابق دارد؟",
        answer:
          "شینگل جدید به‌ندرت با فرسوده کاملاً یکی می‌شود چون رنگ و دانه با زمان تغییر می‌کند. انتخاب محصول مناسب رنگ تضاد را کم می‌کند و انتظارات صادقانه پیش از کار بحث می‌شود.",
      },
      {
        question: "چند شینگل آسیب‌دیده برای تعمیر موضعی زیاد است؟",
        answer:
          "عدد ثابت نیست: مهم این است آسیب محدود است و سطح اصلی سقف اطراف هنوز انعطاف کافی برای کار بدون ترک دارد. خرابی مکرر در چند سطح معمولاً برنامه تعویض را اقتصادی‌تر می‌کند.",
      },
      {
        question: "آیا می‌توان شینگل ترک‌خورده را فقط درزبند کرد؟",
        answer:
          "درزبند سطحی عمر کوتاه دارد و تعمیر بعدی را دشوار می‌کند. جایگزینی شینگل همپوشانی هدایت آب را بازمی‌گرداند و معمولاً مقرون‌به‌صرفه‌تر است.",
      },
    ],
  },
  "gutter-cleaning-downspout-flush": {
    title: "تمیزکاری ناودان + شستشوی لوله عمودی",
    shortTitle: "تمیزکاری ناودان",
    eyebrow: "نگهداری زهکشی",
    description:
      "تمیزکاری ناودان و شستشوی لوله‌های عمودی تا زهکشی لبه در بارندگی مترو ونکوور کار کند.",
    definition:
      "تمیزکاری ناودان آوار را از ناودان و لوله‌های عمودی برمی‌دارد تا رواناب سقف بتواند از خط لبه بدون سرریز به فاسیا، فونداسیون یا فضای سبز خارج شود.",
    metaTitle: "تمیزکاری ناودان و لوله عمودی ونکوور بریتیش کلمبیا | رولین روفینگ",
    metaDescription:
      "تمیزکاری حرفه‌ای ناودان و شستشوی لوله عمودی برای خانه‌های مترو ونکوور. بازگرداندن زهکشی لبه و کاهش ریسک سرریز.",
    forWhom: "مالکانی با سرریز، رشد گیاه در ناودان یا لکه فاسیا پس از باران عادی.",
    warningSigns: [
      "آب روی صورت ناودان",
      "گیاه در شیار",
      "پاشش به فونداسیون",
      "فاسیا لکه‌دار",
      "لوله عمودی که در تست تخلیه نمی‌کند",
    ],
    evaluates: [
      "حجم آوار",
      "شیب شیار",
      "وضعیت خروجی و امتداد",
      "اتصال فاسیا",
      "آیا فلز از قبل خراب شده",
    ],
    causes: [
      "برگ و آوار خزه",
      "انباشت سوزن کاج",
      "شیب نشسته",
      "صفحه‌های گرفته",
      "تمیزکاری فصلی از دست رفته",
    ],
    methodology: [
      "پاکسازی شیارها",
      "شستشوی لوله عمودی",
      "بررسی خروجی و امتداد",
      "ثبت وضعیت فلز",
      "توصیه محافظ یا تعویض فقط در صورت توجیه",
    ],
    materials: ["ابزار تمیزکاری", "تجهیزات شستشو", "بست پایه فقط در صورت درج در شرح کار"],
    bcClimate: "باران مکرر یعنی ناودان گرفته سریع فاسیا و فونداسیون را خیس می‌کند در لوئر مینلند.",
    whenAppropriate: "وقتی فلز هنوز سالم است و آوار مسئله اصلی است.",
    whenOtherService: "ناودان زنگ‌زده، جدا یا دائماً کوچک ممکن است تعویض بخواهد.",
    solutionCategories: [
      { title: "تمیزکاری شیار", body: "حذف آوار آلی و دانه‌ای از ناودان." },
      { title: "شستشوی لوله عمودی", body: "بازگرداندن مسیر تخلیه پیوسته." },
      { title: "یادداشت وضعیت", body: "علامت‌گذاری فلز یا فاسیا خراب کشف‌شده در تمیزکاری." },
    ],
    repairVsReplace: {
      repair: "تمیزکاری وقتی شیارها ساختاری سالم‌اند.",
      replace: "تعویض وقتی فلز یا شیب پس از تمیزکاری عمل نمی‌کند.",
    },
    scopeFactors: ["متراژ خطی", "طبقات و دسترسی", "نوع آوار", "تعداد لوله عمودی", "نیازهای ایمنی"],
    process: [
      "ارتفاع خانه، طول ناودان و یادداشت دسترسی",
      "پاکسازی برگ، سوزن و آوار دانه‌ای",
      "شستشوی هر لوله عمودی و تأیید تخلیه آزاد",
      "ثبت وضعیت فلز، شیب و فاسیا",
      "گزارش آنچه تمیزکاری به‌تنهایی حل نمی‌کند",
    ],
    publicPriceHint:
      "قیمت‌گذاری معمولاً به متراژ خطی، تعداد طبقات، بار آوار و نیازهای دسترسی و ایمنی بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تمیزکاری ناودان + شستشوی لوله عمودی چیست؟",
        answer: "حذف آوار از ناودان و لوله‌های عمودی تا رواناب سقف از خط لبه بدون سرریز خارج شود.",
      },
      {
        question: "ناودان در مترو ونکوور چند وقت یک‌بار تمیز شود؟",
        answer:
          "بیشتر خانه‌های محلی حداقل یک تمیزکاری پاییز پس از ریزش برگ می‌خواهند؛ املاک زیر کاج اغلب بازدید دوم می‌خواهند. سرریز در باران عادی یعنی دیر کرده‌اید.",
      },
      {
        question: "اگر تمیزکاری ناودان را رد کنم چه می‌شود؟",
        answer:
          "ناودان مسدود به فاسیا و سایدینگ سرریز می‌کند، آب را به فونداسیون می‌ریزد و می‌تواند آب را زیر لبه سقف برگرداند. در این اقلیم کار نگهداری کوچک اغلب به پوسیدگی فاسیا تبدیل می‌شود.",
      },
      {
        question: "آیا لوله عمودی را هم می‌شویید یا فقط ناودان؟",
        answer:
          "هر دو. شیار تمیز که به لوله عمودی مسدود تخلیه می‌کند همچنان سرریز می‌کند. هر لوله شسته و تخلیه تأیید می‌شود.",
      },
      {
        question: "ناودان تمیز هم سرریز می‌کند: بعد چه؟",
        answer:
          "این به شیب، اندازه یا خروجی اشاره دارد نه آوار. یادداشت وضعیت گنجانده می‌شود و اگر فلز یا هندسه عمل نکند، تعمیر یا تعویض با دلایل مستند توصیه می‌شود.",
      },
    ],
  },
  "storm-tarping-emergency-dry-in": {
    title: "پارچه طوفانی / آب‌بندی اضطراری",
    shortTitle: "پوشش موقت پس از طوفان",
    eyebrow: "ضدآب موقت",
    description: "ضدآب موقت پس از آسیب باد، درخت یا طوفان تا کار دائم سقف ادامه یابد.",
    definition:
      "پارچه طوفانی و آب‌بندی اضطراری موقتاً بازشوها یا نواحی آسیب سقف را از آب‌وهوا محافظت می‌کند تا آسیب داخلی محدود شود در حالی که تعمیر دائم برنامه‌ریزی می‌شود.",
    metaTitle: "پارچه طوفانی و آب‌بندی اضطراری ونکوور | رولین روفینگ",
    metaDescription:
      "پارچه اضطراری سقف و آب‌بندی موقت پس از آسیب طوفان یا درخت در مترو ونکوور. تثبیت بازشو تا تعمیر دائم برنامه‌ریزی شود.",
    forWhom: "مالکانی با عرشه باز، برخورد درخت یا نواحی بزرگ گم‌شده پس از طوفان.",
    warningSigns: [
      "عرشه باز پس از برخورد درخت",
      "نواحی بزرگ گم‌شده",
      "ورود باران داخلی از بازشوی جدید",
      "آوار ناپایدار روی سقف",
      "نیاز به حفاظت موقت پیش از مصالح دائم",
    ],
    evaluates: [
      "ایمنی و دسترسی",
      "اندازه و محل بازشو",
      "ریسک داخلی",
      "پنجره آب‌وهوایی",
      "مسیر به تعمیر دائم",
    ],
    causes: [
      "طوفان باد",
      "شکست درخت",
      "از دست رفتن ناگهانی پوشش",
      "آسیب ضربه",
      "خرابی جزئی مصالح پیر",
    ],
    methodology: [
      "ارزیابی ایمنی اول",
      "پاک‌سازی فقط به‌اندازه‌ای که برای ایمن‌سازی لازم است",
      "نصب ضدآب موقت",
      "مستندسازی وضعیت",
      "برنامه مسیر تعمیر دائم",
    ],
    materials: ["پارچه و غشای موقت", "بست امن مناسب کار موقت", "حفاظت بازشو و لبه"],
    bcClimate:
      "طوفان‌های اقیانوس آرام می‌توانند سریع سقف را باز کنند؛ آب‌بندی موقت آسیب ثانویه داخلی را تا زمان تعیین تعمیر دائمی محدود می‌کند.",
    whenAppropriate: "وقتی بازشو یا ناحیه ناپایدار باید قبل از کار دائم ضدآب شود.",
    whenOtherService: "آب‌بندی موقت جایگزین تعمیر یا تعویض ساختار سقف آسیب‌دیده نیست.",
    solutionCategories: [
      { title: "ضدآب موقت", body: "محدود کردن ورود آب‌وهوا از بازشوهای طوفان." },
      { title: "تثبیت", body: "ایمن کردن مصالح شل که آسیب بیشتر تهدید می‌کند." },
      { title: "مستندسازی", body: "ثبت وضعیت برای برنامه‌ریزی تعمیر دائم." },
    ],
    repairVsReplace: {
      repair: "آب‌بندی موقت اول وقتی کار دائم فوری ممکن نیست.",
      replace: "تعمیر یا تعویض دائم وقتی ساختار سقف درست بازگردانده شود.",
    },
    scopeFactors: [
      "اندازهٔ بازشو",
      "دسترسی و ارتفاع",
      "آب‌وهوا",
      "میزان نفوذ آب به فضای داخلی",
      "خطر ریزش آوار",
    ],
    process: [
      "تماس با عکس و جزئیات ایمنی در صورت امکان",
      "ارزیابی خطر و دسترسی در محل",
      "تثبیت آوار فقط برای پوشش ایمن",
      "نصب امن ضدآب موقت روی بازشو",
      "مستندسازی و برنامه تعمیر دائم",
    ],
    publicPriceHint:
      "شرح کار آب‌بندی موقت به اندازه بازشو، ارتفاع سقف، آب‌وهوا حین کار و خطر آوار بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "پارچه طوفانی / آب‌بندی اضطراری چیست؟",
        answer:
          "محافظت موقت بازشوها یا نواحی آسیب سقف از آب‌وهوا تا آسیب داخلی محدود شود در حالی که تعمیر دائم برنامه‌ریزی می‌شود.",
      },
      {
        question: "درخت به سقف خورد: اول چه کنم؟",
        answer:
          "افراد را از اتاق‌های زیر دور نگه دارید، روی سقف نروید و از زمین و داخل عکس بگیرید. سپس برای پوشش و ایمن‌سازی حرفه‌ای تماس بگیرید: شاخه ناپایدار و آسیب سازه پنهان سقف طوفان‌زده را خطرناک می‌کند.",
      },
      {
        question: "پارچه سقف چقدر می‌تواند بماند؟",
        answer:
          "پارچه حفاظت آب‌وهوایی موقت است نه سقف. عمر عملی هفته‌ها تا چند ماه بسته به میزان قرارگیری در معرض باد و باران، بست و طوفان زمستان است. تعمیر دائم باید به‌محض امکان برنامه شود.",
      },
      {
        question: "آیا بیمه پارچه اضطراری را پوشش می‌دهد؟",
        answer:
          "بسیاری از بیمه‌نامه‌های ملک، محافظت موقت و معقول پس از خسارت ناگهانی را اقدامی برای جلوگیری از خسارت بیشتر در نظر می‌گیرند؛ بااین‌حال پوشش دقیق به مفاد بیمه‌نامهٔ شما بستگی دارد. رولین وضعیت موجود و کار انجام‌شده را مستند می‌کند، اما دربارهٔ پوشش بیمه‌ای مشاوره نمی‌دهد.",
      },
      {
        question: "آیا آب‌بندی موقت جایگزین تعمیر سقف است؟",
        answer:
          "خیر. آب‌بندی موقت، آسیب داخلی را محدود می‌کند در حالی که تعمیر یا تعویض درست برنامه می‌شود. ماندن طولانی‌مدت معمولاً به ورود تدریجی آب منجر می‌شود.",
      },
    ],
  },
  "skylight-leak-flashing-repair": {
    title: "تعمیر نشت / فلاشینگ نورگیر",
    shortTitle: "تعمیر نورگیر",
    eyebrow: "رابط‌های نورگیر",
    description: "تعمیر یا فلاشینگ مجدد نورگیرهای نشت‌دار و سقف اطراف آن‌ها.",
    definition:
      "تعمیر نشت و فلاشینگ نورگیر ورود آب در لبه نورگیر، فلاشینگ و سقف مجاور را برطرف می‌کند نه اینکه فرض کند فقط شیشه علت است.",
    metaTitle: "تعمیر نشت و فلاشینگ نورگیر ونکوور | رولین روفینگ",
    metaDescription:
      "تشخیص نشت نورگیر و تعمیر فلاشینگ لبه برای خانه‌های مترو ونکوور. اصلاح رابط سقف اطراف نورگیر، نه فقط درز شیشه.",
    forWhom: "مالکانی با لکه زیر نورگیر، فلاشینگ لبه خراب یا ابهام بین تراکم و نشت واقعی.",
    warningSigns: [
      "لکه زیر چاه نور",
      "رنگ پوسته در شفت نور",
      "نور روز در گوشه لبه",
      "فلاشینگ پله‌ای خراب کنار واحد",
      "آب ایستاده علیه لبه در سقف کم‌شیب",
    ],
    evaluates: [
      "نوع و سن نورگیر",
      "وضعیت لبه و فلاشینگ",
      "یکپارچگی پوشش مجاور",
      "الگوی تراکم داخلی",
      "آیا تعویض واحد لازم است",
    ],
    causes: [
      "شکست فلاشینگ لبه",
      "یکپارچه‌سازی نادرست با شینگل یا غشا",
      "درزبند پیر",
      "تراکم اشتباه گرفته شده برای نشت",
      "حرکت در بازشوی قاب",
    ],
    methodology: [
      "تفکیک نشت و تراکم در صورت امکان",
      "بازرسی فلاشینگ لبه و سقف مجاور",
      "فلاشینگ مجدد یا تعمیر جزئیات سازگار",
      "رسیدگی به پوشش اطراف در صورت نیاز",
      "توصیه تعویض واحد فقط در صورت توجیه",
    ],
    materials: [
      "فلاشینگ لبه",
      "جزئیات زیرلایه یا غشای سازگار",
      "وصله شینگل یا غشای اطراف",
      "درزبند فقط به‌عنوان اقدام ثانویه",
    ],
    bcClimate:
      "باران بادی سریع لبه‌های ضعیف نورگیر را در ساحل بریتیش کلمبیا پیدا می‌کند؛ وضعیت شیشه به‌تنهایی همه نشت را توضیح نمی‌دهد.",
    whenAppropriate: "وقتی واحد نورگیر قابل سرویس است و خرابی در فلاشینگ یا سقف مجاور است.",
    whenOtherService:
      "قاب خراب، واحد مهر و موم‌شده مزمن یا خرابی گسترده سقف ممکن است شرح کار متفاوتی بخواهد.",
    solutionCategories: [
      { title: "فلاشینگ مجدد لبه", body: "بازگرداندن رابط فلز و غشا در لبه نورگیر." },
      { title: "تعمیر پوشش مجاور", body: "اصلاح شینگل یا غشایی که دیگر با واحد یکپارچه نیست." },
      {
        title: "بررسی نشت در برابر تراکم",
        body: "کمک به تفکیک نشت ساختار سقف از علائم رطوبت داخلی.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر وقتی واحد و لبه جزئیات بادوام می‌پذیرند.",
      replace: "تعویض واحد یا سقف وقتی ساختار سقف قابل اتکا نیست.",
    },
    scopeFactors: [
      "تعداد و اندازه نورگیر",
      "نوع سیستم سقف",
      "ارتفاع و وضعیت لبه",
      "دسترسی",
      "پوشش داخلی در معرض ریسک",
    ],
    process: [
      "الگوی لکه و سن نورگیر را توضیح دهید",
      "بازرسی لبه، فلاشینگ و سقف مجاور",
      "تفکیک شواهد نشت از تراکم",
      "فلاشینگ مجدد یا تعمیر جزئیات خراب",
      "تأیید رابط و مستندسازی کار",
    ],
    publicPriceHint:
      "کار نورگیر به ازای زمینه هر واحد قیمت‌گذاری می‌شود: وضعیت لبه، نوع سیستم سقف، نیاز فلاشینگ و دسترسی. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر نشت / فلاشینگ نورگیر چیست؟",
        answer:
          "رسیدگی به ورود آب در لبه نورگیر، فلاشینگ و سقف مجاور نه فرض اینکه فقط شیشه علت است.",
      },
      {
        question: "نورگیرم نشت می‌کند یا تراکم است؟",
        answer:
          "تراکم قطرات ریز یا مه روی شیشه و قاب است، معمولاً در هوای سرد و اتاق مرطوب. نشت از گوشه لبه یا لبه بالایی ردیابی می‌کند و اغلب دیوار چاه نور را لکه می‌کند.",
      },
      {
        question: "آیا نورگیر نشت‌دار یعنی نورگیر جدید؟",
        answer:
          "لزوماً نه. بسیاری از نشت‌ها از فلاشینگ یا سقف اطراف است نه خود واحد. اگر مهر شیشه شکسته یا قاب فرسوده است تعویض بحث می‌شود؛ در غیر این صورت فلاشینگ مجدد معمولاً کافی است.",
      },
      {
        question: "آیا نورگیرها در تعویض و اجرای دوبارهٔ سقف تعویض شوند؟",
        answer:
          "اغلب لحظه عملی است: فلاشینگ باید دوباره انجام شود، دسترسی آماده است و واحد پیر احتمالاً از سقف جدید عمر بیشتری نمی‌برد. واحد سالم می‌تواند بماند و درست فلاش شود.",
      },
      {
        question: "چرا فقط در باران بادی نشت می‌کند؟",
        answer:
          "باران هدایت‌شده با باد جزئیات فلاشینگ را متفاوت بارگذاری می‌کند: می‌تواند روی همپوششی‌ها بالا رود و از جزئیاتی که باران عمودی را خوب هدایت می‌کنند وارد شود. این الگو اغلب هندسه فلاشینگ را نشان می‌دهد نه مهر شیشه.",
      },
    ],
  },
  "chimney-flashing-repair": {
    title: "تعمیر فلاشینگ دودکش",
    shortTitle: "فلاشینگ دودکش",
    eyebrow: "رابط‌های دودکش",
    description: "فلاشینگ پله‌ای، فلاشینگ رویی و اصلاح نشت مرتبط با دودکش در سقف‌های مترو ونکوور.",
    definition:
      "تعمیر فلاشینگ دودکش رابط‌های فلزی هدایت آب را بازمی‌گرداند جایی که دودکش آجری یا قاب‌دار به سطوح سقف می‌رسد.",
    metaTitle: "تعمیر فلاشینگ دودکش مترو ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر فلاشینگ پله‌ای و فلاشینگ رویی دودکش برای سقف‌های مترو ونکوور. اصلاح مسیر نشت دودکش به سقف با جزئیات فلزی درست.",
    forWhom: "مالکانی با نشت گوشه دودکش، فلاشینگ رویی زنگ‌زده یا جزئیات کرکت فرسوده.",
    warningSigns: [
      "لکه گوشه دودکش",
      "رگه زنگ از فلاشینگ رویی",
      "مشکلات آهک با نشت زیر",
      "کرکت بالادست گم‌شده در دودکش عریض",
      "قیر سطحی قبلی در پای دودکش",
    ],
    evaluates: [
      "اندازه و هندسه دودکش",
      "وضعیت فلاشینگ پله‌ای و فلاشینگ رویی",
      "نیاز کرکت بالادست",
      "رابط آهک",
      "وضعیت پوشش سقف اطراف",
    ],
    causes: [
      "فلاشینگ پله‌ای نادرست بافته نشده",
      "فلاشینگ رویی از آهک جدا شده",
      "حرکت حرارتی",
      "کرکت بالادست گم‌شده",
      "سن و خوردگی",
    ],
    methodology: [
      "بازرسی همه وجه دودکش و تقاطع سقف",
      "باز کردن فقط برای بازگرداندن دور فلزی",
      "نصب صحیح پله‌ای و فلاشینگ رویی",
      "افزودن یا بازگرداندن کرکت در صورت نیاز",
      "یکپارچه‌سازی مجدد پوشش اطراف",
    ],
    materials: [
      "فلاشینگ پله‌ای و فلاشینگ رویی",
      "مصالح قاب/پوشش کرکت در صورت نیاز",
      "زیرلایه سازگار",
      "آهک یا درزبند فقط به‌عنوان جزئیات ثانویه مناسب",
    ],
    bcClimate:
      "گوشه‌های دودکش مناطق پرتنش مرطوب در باران بادی لوئر مینلند هستند؛ قیر سطحی جایگزین دور فلزی نیست.",
    whenAppropriate: "وقتی دودکش و سطح اصلی سقف اطراف فلاشینگ صحیح را پشتیبانی می‌کنند.",
    whenOtherService:
      "اگر سطح اصلی سقف در پایان عمر است، فلاشینگ مجدد اغلب بهتر با برنامه تعویض ترکیب شود.",
    solutionCategories: [
      { title: "بازگردانی فلاشینگ پله‌ای", body: "بازگرداندن دور فلزی بافته با پوشش سقف." },
      {
        title: "اصلاح فلاشینگ رویی",
        body: "اصلاح لبهٔ بالایی فلز در بندکشی یا جزئیات نمای ساختمان.",
      },
      {
        title: "کرکت و جزئیات انحراف",
        body: "بهبود انحراف آب بالادست در دودکش‌های عریض در صورت نیاز.",
      },
    ],
    repairVsReplace: {
      repair: "فلاشینگ مجدد دودکش وقتی سطح اصلی سقف قابل سرویس است.",
      replace:
        "ترکیب با تعویض و اجرای دوبارهٔ سقف وقتی سطح اصلی سقف یکپارچه‌سازی بادوام نمی‌پذیرد.",
    },
    scopeFactors: ["عرض دودکش", "شیب سقف", "وضعیت آهک", "دسترسی", "سن پوشش اطراف"],
    process: [
      "عکس دودکش و لکه‌های داخلی را بفرستید",
      "بازرسی همه وجه‌ها و تقاطع سقف",
      "شرح کار مکتوب برای دور فلزی بازسازی‌شونده",
      "نصب صحیح پله‌ای، فلاشینگ رویی و کرکت",
      "یکپارچه‌سازی پوشش و بازبینی نتیجه",
    ],
    publicPriceHint:
      "کار دودکش حول عرض دودکش، شیب سقف، وضعیت آهک و میزان بلند کردن پوشش اطراف قیمت‌گذاری می‌شود. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر فلاشینگ دودکش چیست؟",
        answer: "بازگرداندن رابط‌های فلزی هدایت آب جایی که دودکش به سطوح سقف می‌رسد.",
      },
      {
        question: "چرا دودکش با سقف جدید هم نشت می‌کند؟",
        answer:
          "فلاشینگ دودکش نقطه ضعیف رایج پس از تعویض و اجرای دوبارهٔ سقف است: اگر فلاشینگ رویی بازاستفاده یا پله‌ای درست بافته نشود، پوشش جدید آب را مستقیم به خرابی قدیمی می‌فرستد.",
      },
      {
        question: "فلاشینگ رویی چیست و چرا مهم است؟",
        answer:
          "فلاشینگ پله‌ای آب را روی شینگل می‌برد؛ فلاشینگ رویی دور فلز بالایی است که در آهک فرو رفته و پای عمودی پله را می‌پوشاند. بدون خاتمه سالم، آب روی صورت دودکش مستقیم پشت فلز پله می‌رود.",
      },
      {
        question: "آیا به کرکت پشت دودکش نیاز دارم؟",
        answer:
          "دودکش‌های عریض در سمت شیب پایین آب و آوار جمع می‌کنند. کرکت، ریز شیروانی انحرافی، آب را دور می‌فرستد. نیاز به عرض دودکش، شیب و شرایط مشاهده‌شده بستگی دارد.",
      },
      {
        question: "آیا قیر سقف برای فلاشینگ دودکش قابل قبول است؟",
        answer:
          "به‌عنوان توقف اضطراری می‌تواند نشت را کند کند. به‌عنوان تعمیر شکست می‌خورد: جمع می‌شود، ترک می‌خورد و آب را پشت خود نگه می‌دارد. اصلاح پایدار دور فلزی را بازمی‌گرداند.",
      },
    ],
  },
  "gutter-replacement": {
    title: "تعویض ناودان",
    shortTitle: "تعویض ناودان",
    eyebrow: "سیستم‌های زهکشی لبه",
    description: "تعویض ناودان و لوله عمودی آلومینیوم یکپارچه برای زهکشی قابل اتکای لبه.",
    definition:
      "تعویض ناودان مسیرهای جدید ناودان و لوله عمودی با اندازه و شیب مناسب برای هدایت رواناب سقف از پوشش ساختمان نصب می‌کند.",
    metaTitle: "تعویض ناودان مترو ونکوور | رولین روفینگ",
    metaDescription:
      "تعویض ناودان و لوله عمودی آلومینیوم یکپارچه برای خانه‌های مترو ونکوور. شیب، خروجی و عملکرد زهکشی لبه درست.",
    forWhom:
      "مالکانی با سرریز مزمن پس از تمیزکاری، ناودان زنگ‌زده یا جدا، یا آسیب فاسیا از زهکشی خراب.",
    warningSigns: [
      "درزهای شکاف‌دار",
      "ناودان از فاسیا جدا",
      "سرریز مزمن پس از تمیزکاری",
      "سوراخ خوردگی",
      "شیب نادرست غیرقابل اصلاح درجا",
    ],
    evaluates: [
      "متراژ خطی و طبقات",
      "طراحی خروجی",
      "وضعیت فاسیا",
      "رنگ و پروفیل",
      "مسیریابی لوله عمودی",
    ],
    causes: ["پایان عمر فلز", "شیارهای کوچک", "حرکت فاسیا", "بار آوار", "نصب اولیه ضعیف"],
    methodology: [
      "تأیید آمادگی فاسیا",
      "چیدمان شیب و خروجی",
      "نصب مسیر یکپارچه و لوله عمودی",
      "تست مسیر جریان",
      "رسیدگی به پاشش و تخلیه",
    ],
    materials: ["ناودان آلومینیوم یکپارچه", "لوله عمودی و زانو", "آویز و بست", "درپوش و خروجی"],
    bcClimate:
      "حجم بارندگی بالا اندازه و شیب درست ناودان را برای خانه‌های لوئر مینلند ضروری می‌کند.",
    whenAppropriate: "وقتی ناودان موجود حتی پس از تمیزکاری و تعمیر جزئی عمل نمی‌کند.",
    whenOtherService: "اگر شیارها سالم‌اند، تمیزکاری یا محافظ کافی است.",
    solutionCategories: [
      { title: "سیستم لبه یکپارچه", body: "شیارهای جدید با شیب به خروجی‌های کنترل‌شده." },
      { title: "طراحی لوله عمودی", body: "هدایت تخلیه دور از فونداسیون و پیاده‌رو." },
      { title: "هماهنگی فاسیا", body: "رسیدگی به فاسیا نرم پیش از آویز فلز جدید در صورت نیاز." },
    ],
    repairVsReplace: {
      repair: "تعویض وقتی فلز یا هندسه شکست خورده.",
      replace: "تمیز یا تعمیر فقط وقتی سیستم سالم است.",
    },
    scopeFactors: ["متراژ خطی", "ارتفاع/دسترسی", "تعداد خروجی", "انتخاب رنگ", "تعمیر فاسیا"],
    process: [
      "اندازه‌گیری مسیر، خروجی و مسیریابی لوله عمودی",
      "تأیید فاسیا برای آویز جدید",
      "شرح کار مکتوب با انتخاب پروفیل و رنگ",
      "شکل‌دهی و نصب یکپارچه در محل",
      "تست جریان و بازبینی تخلیه",
    ],
    publicPriceHint:
      "قیمت تعویض به متراژ خطی، طبقات و دسترسی، تعداد خروجی و هر تعمیر فاسیا پیش از آن بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعویض ناودان چیست؟",
        answer:
          "نصب مسیرهای جدید ناودان و لوله عمودی با اندازه و شیب مناسب برای هدایت رواناب سقف از پوشش ساختمان.",
      },
      {
        question: "ناودان یکپارچه یعنی چه؟",
        answer:
          "در محل با طول پیوسته برای هر مسیر شکل می‌گیرد، پس درز فقط در گوشه و خروجی است: درز کمتر یعنی نقطه نشت آینده کمتر نسبت به ناودان قطعه‌ای.",
      },
      {
        question: "آیا ناودان همزمان با سقف تعویض شود؟",
        answer:
          "اگر هر دو نزدیک پایان عمرند، همزمانی از آسیب به ناودان جدید در کندن جلوگیری و موقعیت لبه چکه و ناودان را هماهنگ می‌کند. سیستم سالم فقط به‌خاطر سقف نیاز به تعویض ندارد.",
      },
      {
        question: "خانه مترو ونکوور چه اندازه ناودان می‌خواهد؟",
        answer:
          "اندازه به مساحت سقف تخلیه‌شده به هر مسیر، شیب و محل خروجی بستگی دارد نه یک پاسخ واحد. شدت باران محلی مسیرهای کوچک یا کم‌زهکش را زود آشکار می‌کند.",
      },
      {
        question: "چگونه آب لوله عمودی از فونداسیون دور نگه داشته می‌شود؟",
        answer:
          "تخلیه به سمت زهکشی مناسب: امتداد، مدیریت پاشش یا زهکشی زمین موجود، برنامه‌ریزی می‌شود تا آب سقف علیه زیرزمین ریخته نشود.",
      },
    ],
  },
  "gutter-guard-installation": {
    title: "نصب محافظ ناودان",
    shortTitle: "محافظ ناودان",
    eyebrow: "حفاظت در برابر آوار",
    description:
      "سیستم‌های حفاظت برگ، خزه و آوار که فرکانس گرفتگی را در املاک درخت‌کثیف کاهش می‌دهند.",
    definition:
      "نصب محافظ ناودان سیستم حفاظتی روی ناودان اضافه می‌کند تا تجمع برگ و آوار کم شود در حالی که آب همچنان وارد شیار شود.",
    metaTitle: "نصب محافظ ناودان مترو ونکوور | رولین روفینگ",
    metaDescription:
      "نصب محافظ ناودان برای خانه‌های مترو ونکوور با آوار درختی سنگین. کاهش فرکانس گرفتگی روی سیستم‌های ناودان سالم.",
    forWhom: "مالکانی با تمیزکاری مکرر، دسترسی دشوار چندطبقه یا زندگی زیر پوشش درختی سنگین.",
    warningSigns: [
      "تمیزکاری چندبار در سال",
      "دسترسی دشوار چندطبقه",
      "پوشش درختی پرسوزن",
      "گرفتگی لوله عمودی زود پس از تمیزکاری",
      "محدودیت دسترسی نگهداری",
    ],
    evaluates: [
      "وضعیت ساختاری ناودان اول",
      "پروفایل آوار",
      "مناسب بودن محصول",
      "هندسه لبه سقف",
      "انتظارات نگهداری",
    ],
    causes: [
      "فضای سبز متراکم",
      "هندسه سقف که آوار را به شیار می‌ریزد",
      "آوار ریز که صفحه ساده را شکست می‌دهد",
      "فواصل نگهداری نامنظم",
    ],
    methodology: [
      "تأیید سلامت ناودان",
      "انتخاب سیستم حفاظت مناسب",
      "نصب با توجه به ورود آب و لبه",
      "تست جریان",
      "توضیح نیازهای نگهداری باقی‌مانده",
    ],
    materials: ["اجزای سیستم محافظ", "بست سازگار با ناودان", "لوازم لبه در صورت نیاز"],
    bcClimate:
      "درختان ساحلی سوزن و تکه خزه می‌ریزند که برنامه تمیزکاری کم را شکست می‌دهند؛ محافظ کاهش می‌دهد اما همه نگهداری را حذف نمی‌کند.",
    whenAppropriate: "وقتی ناودان ساختاری سالم است و بار آوار مسئله اصلی است.",
    whenOtherService: "ناودان خراب باید پیش از نصب محافظ تعویض شود.",
    solutionCategories: [
      { title: "سیستم‌های حفاظت آوار", body: "کاهش ورود برگ و سوزن به شیار." },
      { title: "بررسی سازگاری", body: "نصب فقط روی ناودان‌هایی که هنوز درست تخلیه می‌کنند." },
      {
        title: "انتظارات نگهداری",
        body: "روشن کردن آنچه محافظ کاهش می‌دهد در برابر آنچه همچنان دوره‌ای نیاز دارد.",
      },
    ],
    repairVsReplace: {
      repair: "نصب محافظ روی ناودان سالم با بار آوار بالا.",
      replace: "تعویض ناودان اول وقتی فلز شکست خورده.",
    },
    scopeFactors: ["متراژ خطی", "نوع محصول", "پروفایل آوار سقف", "دسترسی", "وضعیت ناودان موجود"],
    process: [
      "ارزیابی وضعیت ناودان و پروفایل آوار ملک",
      "توصیه نوع محافظ متناسب با درختان اطراف",
      "شرح کار مکتوب شامل تمیزکاری پیش‌نیاز",
      "نصب محافظ با توجه به ورود آب و لبه",
      "توضیح صادقانه ریتم نگهداری باقی‌مانده",
    ],
    publicPriceHint:
      "قیمت محافظ به نوع محصول، متراژ خطی، هندسه لبه سقف و وضعیت ناودان موجود بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "نصب محافظ ناودان چیست؟",
        answer:
          "افزودن سیستم حفاظتی روی ناودان برای کاهش تجمع برگ و آوار در حالی که آب وارد شیار شود.",
      },
      {
        question: "آیا محافظ یعنی دیگر ناودان تمیز نمی‌کنم؟",
        answer:
          "خیر. فرکانس را کم می‌کند اما نگهداری را حذف نمی‌کند: آوار روی محافظ جمع می‌شود، دره‌ها همچنان مواد متمرکز می‌کنند و خروجی‌ها باید دوره‌ای بررسی شوند.",
      },
      {
        question: "آیا محافظ با سوزن کاج کار می‌کند؟",
        answer:
          "سوزن‌های ریز صفحه ساده سوراخ‌شده را شکست می‌دهند. محصولات مش میکرو زیر کاج بهترند، اگرچه لایه ریز روی محافظ هم گاه به برس نیاز دارد. انتخاب از آنچه واقعاً روی سقف می‌افتد شروع می‌شود.",
      },
      {
        question: "آیا می‌توان روی ناودان موجود نصب کرد؟",
        answer:
          "بله اگر ساختاری سالم، شیب درست و اتصال محکم باشد. نصب روی ناودان خراب مشکل زیرین را قفل می‌کند: وضعیت اول تأیید می‌شود، گاه با تمیزکاری و تنظیم جزئی.",
      },
      {
        question: "آیا محافظ در باران سنگین مشکل ایجاد می‌کند؟",
        answer:
          "سیستم درست انتخاب و نصب‌شده حجم باران محلی را عبور می‌دهد. محافظ نادرست می‌تواند در رگبار شدید سرریز ایجاد کند، به‌ویژه زیر شیب‌های تند: به همین دلیل شیب سقف و سرعت آب در لبه در انتخاب مهم است.",
      },
    ],
  },
  "roof-ventilation-upgrade": {
    title: "ارتقای تهویه سقف",
    shortTitle: "ارتقای تهویه",
    eyebrow: "جریان هوای اتاق زیرشیروانی",
    description:
      "دریچه جعبه‌ای، شیروانی، ورودی سوفیت و اصلاح جریان هوای اتاق زیرشیروانی برای عملکرد سقف و اتاق زیرشیروانی.",
    definition:
      "ارتقای تهویه سقف مسیرهای ورودی و خروجی را بهبود می‌بخشد تا فضاهای اتاق زیرشیروانی رطوبت و گرما را مؤثرتر مدیریت کنند وقتی تعادل موجود ناکافی است.",
    metaTitle: "ارتقای تهویه سقف ونکوور | رولین روفینگ",
    metaDescription:
      "ارتقای تهویه سقف و اتاق زیرشیروانی برای خانه‌های مترو ونکوور. تعادل ورودی سوفیت و خروجی برای مدیریت رطوبت و گرما.",
    forWhom: "مالکانی با تراکم اتاق زیرشیروانی، پیری ناهمگن شینگل، سوفیت مسدود یا خروجی ناکافی.",
    warningSigns: [
      "تراکم یا یخ در اتاق زیرشیروانی",
      "بو کهنه اتاق زیرشیروانی",
      "پیری ناهمگن شینگل",
      "ورودی سوفیت مسدود یا رنگ‌شده",
      "اتاق زیرشیروانی گرم که به رطوبت کمک می‌کند",
    ],
    evaluates: [
      "پیکربندی اتاق زیرشیروانی",
      "دریچه‌های خروجی موجود",
      "تداوم مسیر ورودی",
      "قرارگیری عایق در لبه",
      "محدودیت هندسه سقف",
    ],
    causes: [
      "خروجی ناکافی",
      "ورودی مسدود",
      "عایق روی سوفیت",
      "فن حمام/آشپزخانه به اتاق زیرشیروانی",
      "تعویض و اجرای دوبارهٔ سقف بدون طراحی تهویه",
    ],
    methodology: [
      "ارزیابی تعادل ورودی و خروجی",
      "باز یا بازگردانی ورودی سوفیت در صورت امکان",
      "افزودن یا اصلاح اجزای خروجی",
      "اجتناب از بیش‌تهویه بدون ورودی",
      "هماهنگی با بحث عایق و درزبندی هوا در صورت نیاز",
    ],
    materials: [
      "دریچه شیروانی، جعبه‌ای یا خروجی دیگر طبق مشخصات",
      "اجزای ورودی سوفیت",
      "بافل یا لوازم اتاق زیرشیروانی",
      "فلاشینگ برای نفوذهای جدید",
    ],
    bcClimate:
      "آب‌وهمای خنک و مرطوب بریتیش کلمبیا مدیریت رطوبت اتاق زیرشیروانی را مهم می‌کند؛ تهویه بخشی از سیستم است نه درمان همه نشت‌ها.",
    whenAppropriate:
      "وقتی عدم تعادل جریان هوا به رطوبت اتاق زیرشیروانی یا فرسایش تسریع‌شده پوشش کمک می‌کند.",
    whenOtherService: "ارتقای تهویه جایگزین تعمیر نشت یا همه منابع تراکم نیست.",
    solutionCategories: [
      {
        title: "ارتقای خروجی",
        body: "افزودن یا اصلاح شیروانی، جعبه‌ای یا خروجی دیگر در صورت مناسب.",
      },
      { title: "بازگردانی ورودی", body: "باز کردن یا بهبود مسیرهای ورودی سوفیت مسدود." },
      { title: "بررسی تعادل", body: "تنظیم مسیر ورود و خروج هوا متناسب با حجم فضای زیرشیروانی." },
    ],
    repairVsReplace: {
      repair: "ارتقای تهویه وقتی عدم تعادل مستند است.",
      replace: "دریچه را جایگزین تعمیر ضدآب نکنید.",
    },
    scopeFactors: [
      "حجم اتاق زیرشیروانی",
      "انواع دریچه موجود",
      "دسترسی ورودی",
      "نوع پوشش سقف",
      "دسترسی",
    ],
    process: [
      "بررسی اتاق زیرشیروانی و اجزای تهویه بیرونی",
      "ارزیابی تعادل ورودی و خروجی برای حجم اتاق زیرشیروانی",
      "شرح کار مکتوب برای دریچه، بازگردانی ورودی و بافل",
      "نصب و فلاشینگ اجزای جدید",
      "تأیید مسیرهای جریان هوا بدون انسداد",
    ],
    publicPriceHint:
      "پروژه‌های تهویه بر حجم اتاق زیرشیروانی، انواع دریچه موجود، دسترسی ورودی و پوشش سقف درگیر شرح کار جداگانه برایشان تعیین می‌شود. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "ارتقای تهویه سقف چیست؟",
        answer:
          "بهبود مسیرهای ورودی و خروجی تا فضاهای اتاق زیرشیروانی رطوبت و گرما را مؤثرتر مدیریت کنند وقتی تعادل موجود ناکافی است.",
      },
      {
        question: "علائم تهویه ضعیف اتاق زیرشیروانی چیست؟",
        answer:
          "تراکم یا یخ زیر عرشه در زمستان، بوی کهنه، لکه قارچ، نوک میخ زنگ‌زده و پیری ناهمگن شینگل در شیب‌ها. در ساحل بریتیش کلمبیا علامت غالب رطوبت است نه گرمای تابستان.",
      },
      {
        question: "آیا دریچه بیشتر تراکم اتاق زیرشیروانی را حل می‌کند؟",
        answer:
          "به‌تنهایی نه. خروجی بدون ورودی سوفیت متناسب می‌تواند هوا را از خانه بکشد و گاه رطوبت را بدتر کند. فن حمام به اتاق زیرشیروانی و نشت هوا از فضای زندگی اغلب محرک واقعی‌اند: کل مسیر ارزیابی می‌شود.",
      },
      {
        question: "دریچه شیروانی یا جعبه‌ای: تفاوت چیست؟",
        answer:
          "دریچه شیروانی پیوسته در نوک سقف و به‌طور یکنواخت از اتاق زیرشیروانی خارج می‌کند؛ جعبه‌ای واحدهای مجزا نزدیک شیروانی است. کدام مناسب است به طول شیروانی، هندسه و توزیع ورودی بستگی دارد.",
      },
      {
        question: "آیا تهویه بهتر عمر شینگل را طولانی می‌کند؟",
        answer:
          "تهویه متعادل بار گرما و رطوبت اتاق زیرشیروانی را که پیری شینگل را تسریع می‌کند کاهش می‌دهد. یک عامل بین چند عامل است: میزان قرارگیری در معرض باد و باران، کیفیت مصالح و نصب همچنان مهم‌اند.",
      },
    ],
  },
  "fascia-soffit-repair-replacement": {
    title: "تعمیر / تعویض فاسیا و سوفیت",
    shortTitle: "فاسیا و سوفیت",
    eyebrow: "اجزای لبه",
    description:
      "تعمیر یا تعویض فاسیا، سوفیت و اجزای لبه پوسیده یا آسیب‌دیده که ناودان و جزئیات لبه را پشتیبانی می‌کنند.",
    definition:
      "تعمیر یا تعویض فاسیا و سوفیت ساختار سقفهای لبه را بازمی‌گرداند که لبه سقف را تمام می‌کنند، ناودان را نگه می‌دارند و به تهویه ورودی کمک می‌کنند.",
    metaTitle: "تعمیر فاسیا و سوفیت ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر یا تعویض فاسیا و سوفیت برای خانه‌های مترو ونکوور. بازگرداندن ساختار لبه، ظاهر و مسیرهای ورودی مرتبط.",
    forWhom: "مالکانی با تخته فاسیا نرم، سوفیت فرسوده، جداشدگی ناودان یا دسترسی آفات در لبه.",
    warningSigns: [
      "تخته فاسیا نرم یا پوسیده",
      "پانل سوفیت افتاده",
      "براکت ناودان شل",
      "شکست رنگ با فساد زیرین",
      "سوراخ‌های مورد استفاده آفات",
    ],
    evaluates: [
      "وسعت خطی آسیب",
      "انتخاب مصالح",
      "نیاز تهویه در سوفیت",
      "برنامه اتصال مجدد ناودان",
      "محدودهٔ رنگ‌آمیزی یا پرداخت",
    ],
    causes: [
      "سرریز ناودان چوب را خیس می‌کند",
      "فلز لبه شکست",
      "پایان عمر مصالح اولیه",
      "رطوبت تهویه",
      "آسیب ضربه یا بازسازی",
    ],
    methodology: [
      "سوند و نقشه طول‌های آسیب‌دیده",
      "حذف اجزای خراب",
      "نصب فاسیا/سوفیت جدید",
      "بازگرداندن بازشوهای تهویه طبق طراحی",
      "هماهنگی نصب مجدد ناودان",
    ],
    materials: [
      "تخته یا سیستم روکش فاسیا",
      "پانل سوفیت",
      "بست و تریم",
      "سیستم رنگ یا پرداخت در صورت گنجاندن",
    ],
    bcClimate: "سرریز مزمن ناودان در ماه‌های بارانی محرک رایج پوسیدگی فاسیا در لوئر مینلند است.",
    whenAppropriate: "وقتی اجزای لبه آسیب‌دیده‌اند اما سطح اصلی سقف ممکن است قابل سرویس بماند.",
    whenOtherService:
      "ترکیب با کار لبه سقف یا ناودان وقتی آن سیستم‌ها علت یا علت مجدد آسیب بوده‌اند.",
    solutionCategories: [
      {
        title: "بازگردانی فاسیا",
        body: "جایگزینی تخته‌های پوسیده که ناودان و فلز لبه را نگه می‌دارند.",
      },
      { title: "تعمیر سوفیت", body: "بازگرداندن پانل‌های زیر لبه و بازشوهای ورودی." },
      { title: "هماهنگی لبه", body: "توالی ناودان و جزئیات لبه با اجزای جدید." },
    ],
    repairVsReplace: {
      repair: "تعمیر محلی وقتی آسیب محدود است.",
      replace: "تعویض طولانی‌تر وقتی پوسیدگی پیوسته در لبه‌هاست.",
    },
    scopeFactors: ["متراژ خطی", "انتخاب مصالح", "ارتفاع/دسترسی", "نیاز پرداخت", "تعامل ناودان"],
    process: [
      "سوند و نقشه طول‌های لبه آسیب‌دیده",
      "برنامه جداشدگی و نصب مجدد ناودان",
      "شرح کار مکتوب با انتخاب مصالح و پرداخت",
      "حذف اجزای پوسیده و نصب جدید",
      "بازگرداندن بازشوهای ورودی و آویز مجدد ناودان",
    ],
    publicPriceHint:
      "کار لبه به متراژ خطی، ارتفاع و دسترسی، انتخاب مصالح و پرداخت و نحوه تعامل ناودان با تعمیر بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر / تعویض فاسیا و سوفیت چیست؟",
        answer:
          "بازگرداندن ساختار سقفهای لبه که لبه سقف را تمام می‌کنند، ناودان را نگه می‌دارند و به تهویه ورودی کمک می‌کنند.",
      },
      {
        question: "چه چیزی باعث پوسیدگی تخته فاسیا می‌شود؟",
        answer:
          "توالی محلی معمولاً سرریز ناودان یا لبه چکه شکست که تخته را در فصل بارانی مکرر خیس می‌کند. وقتی فیلم رنگ می‌شکند چوب مرطوب می‌ماند و فساد تسریع می‌شود. رفع منبع آب به اندازه جایگزینی تخته مهم است.",
      },
      {
        question: "آیا می‌توان فاسیا را بدون برداشتن ناودان تعویض کرد؟",
        answer:
          "به‌ندرت خوب. ناودان از فاسیا بسته می‌شود: تعویض درست یعنی جدا و آویز مجدد، که فرصت اصلاح شیب و بستن به ماده سالم جدید است.",
      },
      {
        question: "آیا سوفیت‌ها بخشی از سیستم تهویه اتاق زیرشیروانی‌اند؟",
        answer:
          "در بیشتر خانه‌ها بله، پانل سوفیت بازشوهای ورودی را حمل می‌کند. تعویض باید آن ورودی را حفظ یا بازگرداند؛ مسدود کردن در ارتقای ظاهری بی‌صدا مشکل رطوبت اتاق زیرشیروانی ایجاد می‌کند.",
      },
      {
        question: "چوب، آلومینیوم یا فاسیا کامپوزیت: کدام؟",
        answer:
          "چوب سنتی و قابل تعمیر است اما نگهداری پرداخت می‌خواهد. روکش آلومینیوم روی چوب در برابر آب مقاوم است اما با جزئیات ضعیف رطوبت را به دام می‌اندازد. کامپوزیت‌ها پوسیدگی کمتر با نگهداری کمتر. میزان قرارگیری در معرض باد و باران، بودجه و معماری خانه راهنماست.",
      },
    ],
  },
  "cedar-shake-repair-restoration": {
    title: "تعمیر / بازسازی شیک سدر",
    shortTitle: "تعمیر سدر",
    eyebrow: "سقف‌های چوب ممتاز",
    description: "جایگزینی شیک سدر آسیب‌دیده و تمیزکاری یا بازسازی موضعی روی سقف‌های چوب ممتاز.",
    definition:
      "تعمیر شیک سدر شیک‌های خراب مجزا و جزئیات مرتبط را بازمی‌گرداند وقتی سقف سدر گسترده‌تر ارزش نگهداری دارد.",
    metaTitle: "تعمیر شیک سدر ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر و بازسازی موضعی شیک سدر برای سقف‌های چوب مترو ونکوور. حفظ سدر قابل سرویس با جایگزینی هدفمند شیک.",
    forWhom: "مالکان سقف سدر با شیک ترک‌خورده، گم‌شده یا قوزخورده در نواحی محدود.",
    warningSigns: [
      "شیک منفرد ترک‌خورده یا گم‌شده",
      "قوز موضعی",
      "خزه که رطوبت را روی چوب نگه می‌دارد",
      "لکه بست با خرابی مجزا",
      "سد آوار در بخش‌های کم‌شیب سدر",
    ],
    evaluates: [
      "دسترسی شیک همسان",
      "وضعیت سطح اصلی سقف باقی‌مانده",
      "نیاز تمیزکاری",
      "رابط‌های فلاشینگ",
      "آیا تعویض سیستماتیک نزدیک‌تر است",
    ],
    causes: [
      "چرخه فرابنفش و خیس شدن",
      "رشد زیستی",
      "ضربه",
      "تعمیر قبلی نادرست",
      "پیری سقف چوب ممتاز که هنوز شایسته نگهداری است",
    ],
    methodology: [
      "یافتن شیک همسان",
      "جایگزینی دقیق واحدهای خراب",
      "رسیدگی به آوار و رشد نگه‌دارنده رطوبت",
      "بازبینی فلاشینگ در ناحیه کار",
      "مستندسازی شاخص‌های عمر باقی‌مانده",
    ],
    materials: [
      "شیک سدر با درجه مناسب",
      "وصله زیرلایه در صورت نیاز",
      "بست سازگار",
      "روش تمیزکاری مناسب سقف چوب",
    ],
    bcClimate:
      "اقلیم مرطوب بریتیش کلمبیا برای سقف چوب سخت‌گیر است؛ نگهداری می‌تواند عمر مفید را وقتی سطح اصلی سقف اساساً سالم است طولانی کند.",
    whenAppropriate: "وقتی خرابی‌ها موضعی‌اند و سطح اصلی سقف سدار گسترده‌تر قابل نگهداری است.",
    whenOtherService: "قوز، نشت و شکست زیرلایه در کل سیستم به سمت تعویض کامل سدر می‌رود.",
    solutionCategories: [
      {
        title: "جایگزینی انتخابی شیک",
        body: "تعویض قطعات آسیب‌دیده و حفظ بخش‌های سالم و قابل‌استفادهٔ سقف.",
      },
      { title: "تمیزکاری موضعی", body: "کاهش آوار و رشد نگه‌دارنده رطوبت اطراف ناحیه تعمیر." },
      {
        title: "اولویت‌بندی وضعیت",
        body: "جداسازی سدر قابل نگهداری از سقف‌هایی که بحث تعویض کامل می‌خواهند.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر وقتی سطح اصلی سقف ارزش نگهداری دارد.",
      replace: "تعویض وقتی فرسایش سقف چوب سیستماتیک است.",
    },
    scopeFactors: ["مساحت تعمیر", "تطابق شیک", "دسترسی", "میزان تمیزکاری", "نیاز فلاشینگ مجاور"],
    process: [
      "ارزیابی سطح اصلی سقف، رشد و محل خرابی",
      "یافتن شیک همسان با درجه مناسب",
      "شرح کار مکتوب برای ناحیه تعمیر و تمیزکاری",
      "جایگزینی شیک خراب و رسیدگی به آوار نگه‌دارنده رطوبت",
      "مستندسازی شاخص‌های عمر باقی‌مانده برای برنامه‌ریزی",
    ],
    publicPriceHint:
      "تعمیر سدر به مساحت تعمیر، تطابق شیک، دسترسی، میزان تمیزکاری و نیاز فلاشینگ مجاور بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر / بازسازی شیک سدر چیست؟",
        answer:
          "بازگرداندن شیک‌های خراب مجزا و جزئیات مرتبط وقتی سقف سدر گسترده‌تر ارزش نگهداری دارد.",
      },
      {
        question: "آیا سقف سدرم ارزش تعمیر دارد یا تعویض؟",
        answer:
          "عوامل تعیین‌کننده وضعیت کل سطح اصلی سقف و زیرلایه زیر آن است نه بدترین چند شیک. ترک‌های موضعی روی سطح اصلی سقف سالم و زهکشی خوب به تعمیر می‌روند؛ قوز گسترده، شیک نرم و نشت مکرر در سطح‌ها به برنامه تعویض.",
      },
      {
        question: "چرا بعضی شیک‌ها قوز و ترک می‌خورند؟",
        answer:
          "سدر با چرخه خیس و خشک شدن حرکت می‌کند. شیب‌های آفتابی تهاجمی خشک و ترک می‌شوند؛ شیب‌های سایه‌دار و مرطوب مرطوب می‌مانند و قوز یا خزه می‌گیرند. سن، درجه شیک و بست اولیه زمان ظهور را تحت تأثیر قرار می‌دهند.",
      },
      {
        question: "آیا می‌توان سدر را برای عمر بیشتر درمان کرد؟",
        answer:
          "خالی نگه داشتن سدر از خزه و آوار بالاترین ارزش نگهداری در این اقلیم است. درمان‌های سطحی وجود دارند و مورد به مورد بحث می‌شوند؛ مکمل زهکشی و خشک شدن خوب‌اند نه جایگزین.",
      },
      {
        question: "آیا نواحی تعمیر با رنگ سدر موجود تطابق دارد؟",
        answer:
          "سدر نو در ابتدا طلایی‌رنگ است و با گذشت فصل‌های بارانی به‌تدریج خاکستری می‌شود؛ به همین دلیل بخش‌های تعمیرشده ابتدا مشخص‌اند و کم‌کم با سطح اطراف هماهنگ می‌شوند. اگر یکدستی ظاهر برایتان مهم است، محل تعمیر و نتیجهٔ مورد انتظار پیش از شروع کار با شما مرور می‌شود.",
      },
    ],
  },
  "flat-low-slope-roof-repair": {
    title: "تعمیر سقف مسطح / کم‌شیب",
    shortTitle: "تعمیر مسطح",
    eyebrow: "غشاهای کم‌شیب",
    description: "وصله SBS، مشعل، TPO/EPDM، فلاشینگ و تعمیر نفوذ روی سقف‌های مسطح و کم‌شیب.",
    definition:
      "تعمیر سقف مسطح و کم‌شیب درز، فلاشینگ، نفوذ و خرابی موضعی غشا را بدون لزوم تعویض کل ساختار سقف کم‌شیب برطرف می‌کند.",
    metaTitle: "تعمیر سقف مسطح ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر سقف مسطح و کم‌شیب برای SBS، TPO، EPDM و غشاهای مرتبط در مترو ونکوور. وصله درز و نفوذ در صورت امکان.",
    forWhom: "مالکانی با نشت تجمع آب، درز باز، نشت لبه یا نواحی غشای باز روی سقف کم‌شیب.",
    warningSigns: [
      "باز شدن درز",
      "نشانه سوراخ",
      "شکست محفظه شیب یا جیب نفوذ",
      "تاول با نشت فعال",
      "زهکش یا سرریز که آب را کنترل نمی‌کند",
    ],
    evaluates: [
      "نوع غشا",
      "سازگاری وصله",
      "الگوی زهکشی",
      "وضعیت نفوذ",
      "آیا عایق فراتر از تعمیر محلی خیس است",
    ],
    causes: [
      "پیری غشا",
      "آسیب تردد",
      "استرس تجمع آب",
      "حرکت فلاشینگ در دیوار حائل",
      "وصله‌های ناسازگار قبلی",
    ],
    methodology: [
      "شناسایی سیستم غشا",
      "تعریف روش وصله سازگار",
      "تعمیر فلاشینگ و نفوذ",
      "بهبود جزئیات زهکشی محلی در صورت عملی",
      "توصیه تعویض اگر سیستم تمام شده",
    ],
    materials: [
      "وصله غشای سازگار",
      "فلز و لوازم فلاشینگ",
      "جیب یا جزئیات نفوذ پیش‌ساخته",
      "چسب یا فرآیند حرارتی متناسب با سیستم",
    ],
    bcClimate:
      "فصل مرطوب طولانی و ریسک تجمع آب جزئیات درز و زهکش را در سقف کم‌شیب لوئر مینلند حیاتی می‌کند.",
    whenAppropriate: "وقتی خرابی‌ها موضعی‌اند و سطح اصلی غشا هنوز وصله بادوام می‌پذیرد.",
    whenOtherService: "عایق اشباع یا شکست درز در کل سیستم معمولاً به برنامه تعویض می‌رود.",
    solutionCategories: [
      { title: "وصله غشا", body: "تعمیر سازگار درز، سوراخ و آسیب موضعی سطح اصلی سقف." },
      { title: "جزئیات نفوذ و لبه", body: "بازگرداندن ضدآب در بازشوهای پشت‌بام." },
      {
        title: "اصلاح متمرکز زهکشی",
        body: "رسیدگی به جزئیاتی که آب را علیه نقاط ضعیف نگه می‌دارند.",
      },
    ],
    repairVsReplace: {
      repair: "تعمیر وقتی غشا به‌طور گسترده قابل سرویس است.",
      replace: "تعویض وقتی سیستم اشباع یا مکرر خراب می‌شود.",
    },
    scopeFactors: ["نوع غشا", "مساحت تعمیر", "دسترسی", "تعداد نفوذ", "شرایط زهکشی"],
    process: [
      "شناسایی سیستم غشا و نقاط خرابی",
      "بررسی رطوبت و زهکشی اطراف آسیب",
      "شرح کار مکتوب برای وصله و جزئیات سازگار",
      "اجرای تعمیر با روش متناسب سیستم",
      "بازبینی رفتار زهکشی و مستندسازی کار",
    ],
    publicPriceHint:
      "قیمت تعمیر مسطح به نوع غشا، مساحت تعمیر، تعداد نفوذ، شرایط زهکشی و دسترسی بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر سقف مسطح / کم‌شیب چیست؟",
        answer:
          "رسیدگی به درز، فلاشینگ، نفوذ و خرابی موضعی غشا بدون لزوم تعویض کل ساختار سقف کم‌شیب.",
      },
      {
        question: "چرا نوع غشا برای وصله مسطح مهم است؟",
        answer:
          "هر غشا متفاوت می‌چسبد: SBS آسفالت اصلاح‌شده مشعل یا چسبی است، TPO جوش حرارتی دارد و EPDM به چسب و نوار متکی است. وصله نادرست یا روش اشتباه بادوام نمی‌چسبد: شناسایی اشتباه علت رایج نشت مکرر است.",
      },
      {
        question: "آیا آب ایستاده روی سقف مسطح مشکل است؟",
        answer:
          "آبی که مدت‌ها پس از باران می‌ماند درزها را تحت فشار می‌گذارد، پیری غشا را تسریع و هر نقص کوچک را بزرگ می‌کند. بعضی علل قابل تعمیر: زهکش مسدود، نقاط پایین محلی، در حالی که مشکل شیب سازه‌ای به بحث سطح تعویض می‌رود.",
      },
      {
        question: "آیا سقف مسطح در زمستان قابل تعمیر است؟",
        answer:
          "اغلب بله با تنظیم روش: بعضی مصالح و چسب‌ها محدودیت دما دارند و سطح باید خشک باشد. تثبیت اضطراری تقریباً همیشه ممکن است؛ بعضی جزئیات دائم ممکن است منتظر پنجره آب‌وهوایی مناسب بمانند.",
      },
      {
        question: "چگونه بفهمم عایق زیر غشا خیس است؟",
        answer:
          "علائم شامل رطوبت داخلی مداوم، تاول غشا و نواحی نرم زیر پا است؛ تأیید ممکن است به اسکن رطوبت یا برش آزمایشی نیاز داشته باشد. عایق خیس مهم است چون وصله روی آن رطوبت را به دام می‌اندازد و فساد ادامه می‌یابد.",
      },
    ],
  },
  "metal-roof-repair-maintenance": {
    title: "تعمیر / نگهداری سقف فلزی",
    shortTitle: "تعمیر فلزی",
    eyebrow: "سیستم‌های فلزی",
    description: "تعمیر بست، فلاشینگ، درزبند، پنل و نفوذ برای سیستم‌های سقف فلزی.",
    definition:
      "تعمیر و نگهداری سقف فلزی بست، فلاشینگ، درزبند، آسیب پنل و نفوذ را روی سقف‌های فلزی موجود اصلاح می‌کند در حالی که پنل‌های قابل سرویس حفظ می‌شوند.",
    metaTitle: "تعمیر سقف فلزی ونکوور | رولین روفینگ",
    metaDescription:
      "تعمیر و نگهداری سقف فلزی برای خانه‌های مترو ونکوور. بست، فلاشینگ، درزبند و اصلاح نفوذ روی سیستم‌های فلزی موجود.",
    forWhom: "مالکانی با بست شل، درزبند شکست، نشت نفوذ یا آسیب مجزای پنل روی سقف فلز.",
    warningSigns: [
      "بست بیرون‌زده در سیستم‌های نمایان",
      "درزبند فوم شکست",
      "نشت یقه نفوذ",
      "ترک درزبند در جزئیات",
      "خوردگی لبه برش در فلاشینگ",
    ],
    evaluates: [
      "پروفیل پنل",
      "وضعیت پوشش",
      "نوع بست",
      "جزئیات درزبند و فلاشینگ",
      "آیا آسیب سیستماتیک است",
    ],
    causes: ["چرخه حرارتی", "نوع بست نادرست", "پیری درزبند", "فرورفتگی ضربه", "اثرات شرایط ساحلی"],
    methodology: [
      "شناسایی نوع سیستم",
      "اصلاح بست و درزبند در صورت مناسب",
      "تعمیر فلاشینگ و نفوذ",
      "رسیدگی به آسیب مجزای پنل",
      "اجتناب از تعمیر فقط درزبند ظاهری وقتی جزئیات فلزی لازم است",
    ],
    materials: ["بست و واشر سازگار", "نوار درزبند", "فلز فلاشینگ", "سیستم لمسی فقط در صورت مناسب"],
    bcClimate:
      "شرایط ساحلی و حرکت حرارتی بست و فلاشینگ را به نقاط نگهداری مکرر در سقف فلز تبدیل می‌کند.",
    whenAppropriate: "وقتی پنل‌ها سالم‌اند و مسائل در سطح جزئیات‌اند.",
    whenOtherService: "شکست پوشش در کل سیستم یا پروفیل منسوخ ممکن است به بحث تعویض برود.",
    solutionCategories: [
      { title: "نگهداری بست و درزبند", body: "بازگرداندن جزئیات مکانیکی ضدآب." },
      { title: "تعمیر فلاشینگ و نفوذ", body: "اصلاح رابط‌هایی که آب را راه می‌دهند." },
      { title: "تعمیر موضعی پنل", body: "رسیدگی به آسیب محدود و حفظ بخش‌های سالم سقف." },
    ],
    repairVsReplace: {
      repair: "نگهداری و تعمیر وقتی سطح اصلی سقف فلزی قابل سرویس است.",
      replace: "تعویض وقتی سیستم دیگر از نظر فنی یا اقتصادی قابل نگهداری نیست.",
    },
    scopeFactors: ["نوع سیستم", "دسترسی", "تعداد جزئیات", "وضعیت پوشش", "تراکم نفوذ"],
    process: [
      "شناسایی سیستم پنل و نوع بست",
      "بازرسی بست، درزبند، فلاشینگ و نفوذ",
      "شرح کار مکتوب جداکننده نگهداری و تعمیر",
      "اصلاح جزئیات با اجزای سازگار",
      "بازبینی سقف به‌عنوان سیستم در حال کار",
    ],
    publicPriceHint:
      "کار سقف فلزی بر نوع سیستم، تعداد جزئیات نیازمند اصلاح، وضعیت پوشش و دسترسی شرح کار آن تعیین می‌شود. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعمیر / نگهداری سقف فلزی چیست؟",
        answer:
          "اصلاح بست، فلاشینگ، درزبند، آسیب پنل و نفوذ روی سقف فلزی موجود در حالی که پنل‌های قابل سرویس حفظ می‌شوند.",
      },
      {
        question: "چرا پیچ‌های سقف فلزیم بیرون می‌آیند؟",
        answer:
          "سیستم‌های بست نمایان به پیچ درزبنددار متکی‌اند که با چرخه حرارتی حرکت می‌کند؛ سال‌ها می‌توانند شل شوند و درزبند سخت شود. اصلاح، گاه با جایگزین بزرگ‌تر، نگهداری عادی این سیستم‌هاست نه نشان شکست سقف.",
      },
      {
        question: "سقف فلزم در لوله دریچه نشت می‌کند: رایج است؟",
        answer:
          "نفوذها رایج‌ترین نقاط نشت سقف فلزند. بوت و یقه سریع‌تر از پنل پیر می‌شوند و باید حرکت پنل را بپذیرند. جایگزینی بوت با جزئیات سازگار معمولاً بدون لمس سطح اصلی سقف حل می‌کند.",
      },
      {
        question: "آیا پنل فلزی فرورفته قابل تعمیر است؟",
        answer:
          "فرورفتگی‌های ظاهری که پوشش یا درز را نمی‌شکنند اغلب می‌توانند بمانند. آسیبی که پوشش را می‌شکند، درز باز می‌کند یا آب نگه می‌دارد توجیه تعمیر یا جایگزینی ورق آسیب‌دیده را دارد: در بیشتر پروفیل‌ها عملی است.",
      },
      {
        question: "نگهداری درز ایستاده با فلز پیچ‌دار چه تفاوتی دارد؟",
        answer:
          "درز ایستاده تثبیت را روی گیره پنهان می‌کند: نگهداری درزبند بست روزمره تقریباً ناپدید می‌شود؛ تمرکز به فلاشینگ، نفوذ و درزبند در خاتمه‌ها می‌رود. سیستم پیچ‌دار به بررسی دوره‌ای بست به‌عنوان امر معمول نیاز دارد.",
      },
    ],
  },
  "cedar-shake-roof-replacement": {
    title: "تعویض سقف شیک سدر",
    shortTitle: "تعویض سدر",
    eyebrow: "تعویض و اجرای دوبارهٔ سقف چوب ممتاز",
    description: "تعویض کامل سقف شیک سدر ممتاز برای خانه‌هایی که سقف چوب انتخاب معماری عمدی است.",
    definition:
      "تعویض سقف شیک سدر تعویض کامل سقف با شیک سدر یا اجزای چوب سقف مشخص‌شده، شامل آماده‌سازی زیرسازی و جزئیات درست برای سیستم چوب ممتاز.",
    metaTitle: "تعویض سقف شیک سدر ونکوور بریتیش کلمبیا | رولین روفینگ",
    metaDescription:
      "تعویض کامل سقف شیک سدر برای خانه‌های مترو ونکوور. تعویض و اجرای دوبارهٔ سقف چوب ممتاز با آماده‌سازی زیرسازی و جزئیات درست.",
    forWhom:
      "مالکانی که شخصیت سقف چوب را تازه می‌کنند وقتی فرسایش سیستماتیک سدر تعمیر را دیگر منسجم نمی‌کند.",
    warningSigns: [
      "قوز و ترک در کل سیستم",
      "نشت مکرر در چند سطح",
      "شکست زیرلایه زیر سدر",
      "نگرانی فروش یا برنامه‌ریزی درباره عمر باقی‌مانده",
      "قصد معماری حفظ سقف چوب",
    ],
    evaluates: [
      "درجه و پروفیل شیک",
      "استراتژی زیرلایه",
      "تهویه و خشک شدن",
      "ملاحظات آتش و زمینه پروژه",
      "پیچیدگی هندسه",
    ],
    causes: [
      "تکمیل عمر سرویس کامل",
      "رطوبت مزمن",
      "زیان طوفان در چند سطح",
      "مسائل زیرسازی کشف‌شده در طول زمان",
    ],
    methodology: [
      "ارزیابی زیرسازی و نیازهای ساختار سقف",
      "مشخص کردن سیستم شیک و رویکرد زیرلایه",
      "حفاظت ملک در کندن",
      "نصب سطح اصلی سقف و جزئیات به‌عنوان ساختار سقف چوبی کامل",
      "بازبینی انتظارات نگهداری سدر",
    ],
    materials: [
      "شیک سدر با درجه مشخص‌شده",
      "سیستم‌های زیرلایه",
      "فلاشینگ سازگار با سقف چوب",
      "بست و جزئیات شیروانی",
    ],
    bcClimate:
      "سقف‌های چوب ممتاز در بریتیش کلمبیا به انتظارات نگهداری صادقانه نیاز دارند؛ تعویض ساختار سقف را وقتی تعمیر دیگر با فرسایش سیستماتیک همگام نمی‌ماند بازنشانی می‌کند.",
    whenAppropriate:
      "وقتی مالک سیستم سدر کامل می‌خواهد و سقف چوب موجود در پایان عمر سرویس منسجم است.",
    whenOtherService:
      "خرابی‌های موضعی روی سطح اصلی سقف هنوز قوی ممکن است هنوز تعمیر سدر را مناسب کند.",
    solutionCategories: [
      {
        title: "نصب سطح اصلی سقف سدار کامل",
        body: "شیک جدید به‌عنوان سیستم سقف منسجم نصب می‌شود.",
      },
      {
        title: "آماده‌سازی زیرسازی و زیرلایه",
        body: "رسیدگی به عرشه و لایه‌های هدایت آب زیر چوب.",
      },
      { title: "جزئیات برای سقف چوب", body: "فلاشینگ و لبه برای عملکرد سدر." },
    ],
    repairVsReplace: {
      repair: "تعمیر اگر فقط موضعی است.",
      replace:
        "تعویض و اجرای دوبارهٔ سقف سدر کامل وقتی فرسایش سیستماتیک است و چوب هنوز سیستم انتخابی است.",
    },
    scopeFactors: ["مساحت سقف", "مشخصات شیک", "تعمیر زیرسازی", "دسترسی", "پیچیدگی پروژه"],
    process: [
      "ارزیابی سقف چوب و زیرسازی موجود",
      "مشخص کردن درجه شیک، زیرلایه و جزئیات",
      "شرح کار مکتوب و برنامه حفاظت ملک",
      "کندن، آماده‌سازی زیرسازی و نصب",
      "بازبینی تکمیل و راهنمای نگهداری",
    ],
    publicPriceHint:
      "قیمت تعویض و اجرای دوبارهٔ سقف سدر به مساحت و هندسه سقف، مشخصات شیک، تعمیر زیرسازی و دسترسی بستگی دارد. برای شرح کار مکتوب ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "تعویض سقف شیک سدر چیست؟",
        answer:
          "تعویض کامل سقف با شیک سدر یا اجزای چوب سقف مشخص‌شده، شامل آماده‌سازی زیرسازی و جزئیات درست برای سیستم چوب ممتاز.",
      },
      {
        question: "سقف شیک سدر در بریتیش کلمبیا چقدر دوام می‌آورد؟",
        answer:
          "عمر سرویس با درجه شیک، میزان قرارگیری در معرض باد و باران، جزئیات و بالاتر از همه نگهداری به‌طور گسترده متفاوت است: عدد واحد گمراه‌کننده است. سقف‌های سدر ساخته و نگهداری‌شده خوب در این منطقه اغلب دهه‌ها خدمت می‌کنند؛ نادیده‌گرفته‌شده خیلی زودتر شکست می‌خورند.",
      },
      {
        question: "تفاوت شیک و شینگل سدر چیست؟",
        answer:
          "شیک‌ها شکافته و ضخیم‌تر با صورت بافت‌دار هستند؛ شینگل‌ها اره‌شده، نازک‌تر و یکنواخت‌تر. انتخاب بر ظاهر، جزئیات و هزینه اثر می‌گذارد و در مشخصات حل می‌شود.",
      },
      {
        question: "آیا سقف سدر به زیرلایه خاص نیاز دارد؟",
        answer:
          "سقف چوب به استراتژی زیرلایه نیاز دارد که آب را هدایت کند در حالی که به سدر اجازه خشک شدن می‌دهد: سدر وقتی نفس نمی‌کشد ضعیف عمل می‌کند. لایه بین دوره‌ها و محافظت لبه مناسب عناصر معمول ساختار سقف درست‌اند.",
      },
      {
        question: "آیا سدر هنوز انتخاب معقول سقف در مترو ونکوور است؟",
        answer:
          "برای خانه مناسب همچنان انتخاب ممتاز عمدی است: معماری، شخصیت محله و تعهد مالک به نگهداری مهم‌اند. اقلیم مرطوب انتظارات نگهداری صادقانه می‌خواهد؛ مالکانی که نگهداری کمتر می‌خواهند معمولاً با سیستم ممتاز دیگر بهتر خدمت می‌شوند.",
      },
    ],
  },
  "moss-removal-roof-gutter": {
    title: "حذف خزه + بسته سقف/ناودان",
    shortTitle: "بسته خزه و ناودان",
    eyebrow: "بسته نگهداری",
    description:
      "درمان خزه، حذف آوار، تمیزکاری ناودان و بازرسی بصری سقف برای خانه‌های مترو ونکوور که رشد آلی و زهکشی مسدود فرسایش سقف را تسریع می‌کند.",
    definition:
      "خدمت ترکیبی حذف خزه و نگهداری سقف/ناودان که رشد آلی روی سطح سقف، پاکسازی ناودان و لوله‌های عمودی و ثبت وضعیت قابل‌مشاهده سقف برای برنامه‌ریزی را پوشش می‌دهد.",
    metaTitle: "حذف خزه و تمیزکاری ناودان ونکوور | رولین روفینگ",
    metaDescription:
      "حذف خزه، پاکسازی آوار، تمیزکاری ناودان و بازرسی بصری سقف برای خانه‌های مترو ونکوور. نگهداری متمرکز بر زهکشی و سلامت پوشش.",
    forWhom: "مالکانی با خزه، لیکنه یا آوار سنگین روی شینگل یا در ناودان، به‌ویژه زیر پوشش درختی.",
    warningSigns: [
      "لایه خزه که رطوبت را روی شینگل نگه می‌دارد",
      "سرریز ناودان در باران عادی",
      "رشد آلی روی شیب‌های رو به شمال",
      "سد آوار در دره‌ها",
      "لوله‌های عمودی که دیگر آزاد تخلیه نمی‌کنند",
    ],
    evaluates: [
      "میزان خزه و رشد آلی",
      "وضعیت پوشش زیر رشد",
      "جریان ناودان و لوله عمودی",
      "آوار دره و لبه",
      "آیا آسیب از قبل تعمیر می‌خواهد",
    ],
    causes: [
      "شرایط ساحلی سایه‌دار و مرطوب",
      "آوار درخت",
      "نگهداری ناودان به‌تعویق افتاده",
      "بافت سطح که آلی را نگه می‌دارد",
      "خشک شدن محدود سطح سقف در ماه‌های سرد",
    ],
    methodology: [
      "حفاظت از فضای سبز و دسترسی",
      "حذف خزه و آوار با روش مناسب پوشش",
      "تمیزکاری ناودان و شستشوی لوله عمودی",
      "بازبینی بصری وضعیت",
      "یادداشت اولویت‌های تعمیر کشف‌شده در تمیزکاری",
    ],
    materials: [
      "ابزار نرم مناسب نوع سقف",
      "محصولات درمان تأییدشده در صورت استفاده",
      "ابزار ناودان و تجهیزات شستشو",
    ],
    bcClimate:
      "سایه و باران لوئر مینلند شرایط ایده‌آل خزه ایجاد می‌کند. رشد رطوبت را روی پوشش نگه می‌دارد و در صورت نادیده گرفتن از دست رفتن دانه را تسریع می‌کند.",
    whenAppropriate: "وقتی پوشش سقف اساساً سالم است و رشد، آوار و زهکشی مسئله اصلی‌اند.",
    whenOtherService:
      "شینگل گم‌شده، نشت فعال یا فلاشینگ خراب به خدمات تعمیر نیاز دارند نه فقط تمیزکاری.",
    solutionCategories: [
      { title: "حذف خزه و آلی", body: "کاهش رشد نگه‌دارنده رطوبت روی سطوح مناسب سقف." },
      { title: "تمیزکاری ناودان و لوله عمودی", body: "بازگرداندن زهکشی آزاد در لبه و نقاط تخلیه." },
      { title: "بازبینی بصری سقف", body: "ثبت پوشش آسیب‌دیده پس از پاک شدن آوار." },
    ],
    repairVsReplace: {
      repair: "تمیزکاری برای سقف‌های قابل سرویس که رشد و آوار اصلی‌اند.",
      replace:
        "اگر تمیزکاری شکنندگی گسترده نشان دهد، برنامه تعویض صادقانه‌تر از تمیزکاری مکرر است.",
    },
    scopeFactors: [
      "مساحت سقف",
      "شیب و دسترسی",
      "تراکم پوشش درختی",
      "طول ناودان",
      "شکنندگی پوشش",
      "دفع آوار",
    ],
    process: [
      "نوع سقف، سایه و رطوبت و جزئیات دسترسی را بگویید",
      "بازبینی سایت از میزان رشد، زهکشی و وضعیت پوشش",
      "شرح کار مکتوب برای تمیزکاری، درمان و کار ناودان",
      "حذف دقیق، درمان در صورت مناسب و شستشوی لوله عمودی",
      "یادداشت وضعیت با عکس هر مورد نیازمند تعمیر",
    ],
    publicPriceHint:
      "قیمت‌گذاری به مساحت سقف، شیب، میزان رشد، طول ناودان و دسترسی بستگی دارد. برای قیمت مخصوص پروژه ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "بسته حذف خزه + سقف/ناودان چیست؟",
        answer:
          "خدمت ترکیبی حذف خزه و نگهداری سقف/ناودان که رشد آلی روی سطح سقف، پاکسازی ناودان و لوله‌های عمودی و ثبت وضعیت قابل‌مشاهده سقف برای برنامه‌ریزی را پوشش می‌دهد.",
      },
      {
        question: "آیا شستشوی فشار قوی روش خوبی برای حذف خزه سقف است؟",
        answer:
          "شستشوی فشار بالا معمولاً روی شینگل آسفالتی توصیه نمی‌شود چون دانه محافظ را می‌زدد و می‌تواند آب را زیر پوشش هدایت کند. خزه بهتر با روش‌های مکانیکی ملایم‌تر و در صورت مناسب، درمان متناسب با مصالح سقف برداشته می‌شود.",
      },
      {
        question: "آیا خزه واقعاً به سقف آسیب می‌زند یا فقط ظاهری است؟",
        answer:
          "خزه مستقر رطوبت را روی پوشش نگه می‌دارد، لبه شینگل را بلند می‌کند و از دست رفتن دانه را تسریع می‌کند. روی سدر چوب را مرطوب نگه می‌دارد و پوسیدگی را سرعت می‌بخشد. حذف زودهنگام بسیار کم‌هزینه‌تر از تعمیر فرسایشی است که ایجاد می‌کند.",
      },
      {
        question: "سقف‌های مترو ونکوور چند وقت یک‌بار باید از خزه پاک شوند؟",
        answer:
          "به سایه، پوشش درختی و جهت سقف بستگی دارد: شیب‌های شمالی و سایه‌دار سنگین سریع‌تر دوباره رشد می‌کنند. بسیاری از خانه‌های محلی از نگاه سالانه و تمیزکاری وقتی رشد شروع به مات شدن می‌کند بهره می‌برند، نه برنامه ثابت.",
      },
      {
        question: "آیا حذف خزه از سرریز ناودان جلوگیری می‌کند؟",
        answer:
          "اغلب دو مورد با هم می‌روند: تکه‌های خزه و آوار سقف از علل رایج گرفتگی ناودان‌اند. این بسته هر دو را پاک می‌کند تا زهکشی بر اساس شایستگی خودش قضاوت شود. اگر پس از تمیزکاری هنوز سرریز باشد، ممکن است فلز یا شیب نیاز به توجه داشته باشد.",
      },
    ],
  },
  "roof-health-check-maintenance": {
    title: "بررسی سلامت سقف + نگهداری",
    shortTitle: "بررسی سلامت",
    eyebrow: "ابتدا وضعیت",
    description:
      "بازرسی، عکس، کار جزئی درزبند یا نگهداری و گزارش وضعیت سقف برای برنامه‌ریزی آگاهانه.",
    definition:
      "بررسی سلامت سقف ارزیابی بصری ساختاریافته پوشش، فلاشینگ، زهکشی و اجزای مرتبط قابل دسترس است، معمولاً همراه مستندسازی عکس و موارد نگهداری محدود توافق‌شده از پیش.",
    metaTitle: "بررسی سلامت و نگهداری سقف ونکوور | رولین روفینگ",
    metaDescription:
      "بررسی سلامت سقف، عکس و اولویت‌های نگهداری برای مالکان مترو ونکوور. مستندسازی وضعیت روشن پیش از تصمیم تعمیر یا تعویض.",
    forWhom:
      "مالکانی که برای خرید یا فروش، بازسازی، علائم اولیه یا خط پایه مستند پیش از فصل بارانی آماده می‌شوند.",
    warningSigns: [
      "عدم قطعیت درباره سن یا وضعیت باقی‌مانده سقف",
      "لکه‌های جزئی بدون منبع روشن",
      "آوار فصلی و تجمع خزه",
      "درزبند پیر در نفوذها",
      "نیاز به خط پایه برنامه‌ریزی مکتوب",
    ],
    evaluates: [
      "وضعیت پوشش به تفکیک سطح",
      "فلاشینگ و نفوذها",
      "زهکشی و رابط ناودان",
      "اجزای تهویه قابل‌مشاهده",
      "اولویت‌بندی نگهداری در برابر تعمیر",
    ],
    causes: [
      "پیری عادی بدون مستندسازی",
      "نگهداری به‌تعویق افتاده",
      "شرایط آب‌وهوایی همان ملک",
      "کار قبلی با کیفیت نامعلوم",
      "نیاز تصمیم خریدار یا بازسازی",
    ],
    methodology: [
      "بررسی نگرانی‌ها و سابقه قابل دسترس",
      "بازرسی سیستماتیک نواحی بیرونی سقف",
      "عکس از شرایط نماینده",
      "تکمیل فقط نگهداری جزئی توافق‌شده",
      "ارائه خلاصه وضعیت روشن و گزینه‌ها",
    ],
    materials: [
      "مستندسازی عکس",
      "درزبند یا بست جزئی در صورت گنجاندن",
      "پاکسازی آوار محدود در صورت درج در شرح کار",
      "یادداشت وضعیت مکتوب",
    ],
    bcClimate:
      "باران فصلی بررسی سلامت پیش از زمستان و پس از طوفان را برای یافتن زهکشی مسدود، مصالح بلند شده و خرابی اولیه فلاشینگ مفید می‌کند.",
    whenAppropriate: "وقتی قبل از تعهد به تعمیر، تعویض یا بررسی بیشتر به وضوح نیاز دارید.",
    whenOtherService: "نشت فعال بزرگ یا بازشوهای طوفانی باید به مسیر اضطراری یا تعمیر برود.",
    solutionCategories: [
      {
        title: "بازرسی مستند",
        body: "بررسی ساختاریافته آنچه روی ساختار سقف قابل‌مشاهده و دسترس است.",
      },
      { title: "نگهداری جزئی", body: "کار درزبند یا آوار محدود فقط وقتی گنجانده و مناسب باشد." },
      { title: "خلاصه برنامه‌ریزی", body: "جداسازی نگرانی‌های فوری از موارد پایش میان‌مدت." },
    ],
    repairVsReplace: {
      repair: "بسیاری از یافته‌ها نگهداری یا تعمیر موضعی‌اند نه محرک تعویض کامل.",
      replace: "تعویض فقط وقتی بحث می‌شود که وضعیت مشاهده‌شده مسیر گسترده‌تر را پشتیبانی کند.",
    },
    scopeFactors: [
      "اندازه و پیچیدگی سقف",
      "دسترسی",
      "عمق مستندسازی عکس",
      "موارد نگهداری گنجانده‌شده",
      "تعداد سیستم‌های سقف در ملک",
    ],
    process: [
      "رزرو بررسی سلامت و توضیح نگرانی‌ها",
      "بازرسی بیرونی سیستماتیک با مستندسازی عکس",
      "تکمیل موارد نگهداری جزئی توافق‌شده در همان بازدید",
      "خلاصه وضعیت مکتوب با اولویت‌های رتبه‌بندی‌شده",
      "گفت‌وگوی پیگیری درباره تعمیرات توصیه‌شده",
    ],
    publicPriceHint:
      "شرح کار بررسی سلامت به اندازه و پیچیدگی سقف، دسترسی و میزان مستندسازی و نگهداری جزئی گنجانده‌شده بستگی دارد. ارزیابی درخواست کنید.",
    faqs: [
      {
        question: "بررسی سلامت سقف + نگهداری چیست؟",
        answer:
          "بررسی سلامت سقف ارزیابی بصری ساختاریافته پوشش، فلاشینگ، زهکشی و اجزای مرتبط قابل دسترس است، معمولاً همراه مستندسازی عکس و موارد نگهداری محدود توافق‌شده از پیش.",
      },
      {
        question: "چند وقت یک‌بار باید سقف بازرسی شود؟",
        answer:
          "ریتم مفید محلی بررسی پیش از باران پاییز، به‌علاوه پس از هر طوفان باد شدید است. خانه‌های زیر پوشش درختی سنگین یا سقف در نیمه دوم عمر مورد انتظار از توجه بیشتر بهره می‌برند.",
      },
      {
        question: "پس از بررسی سلامت سقف چه دریافت می‌کنم؟",
        answer:
          "خلاصه مستند با عکس از وضعیت پوشش، فلاشینگ، زهکشی و تهویه، تفکیک‌شده به موارد نیازمند تعمیر فوری، موارد پایش و فرسایش عادی.",
      },
      {
        question: "آیا بررسی سقف قبل از خرید یا فروش خانه ارزش دارد؟",
        answer:
          "بله، سقف یکی از پرهزینه‌ترین اجزای ساختمان برای نوسازی است. این بررسی وضعیت سقف است، نه بازرسی رسمی پیش از خرید.",
      },
      {
        question: "آیا مشکلات یافت‌شده در بازرسی را تعمیر می‌کنید؟",
        answer:
          "موارد نگهداری کوچک توافق‌شده می‌تواند در همان بازدید انجام شود. یافته‌های بزرگ‌تر مستند و جداگانه شرح کار جداگانه برایشان تعیین می‌شود.",
      },
    ],
  },
} as const satisfies Record<ServiceSlug, ServiceContentTranslation>;

export const serviceAreas = {
  vancouver: {
    intro:
      "خانه‌های ونکوور از واحدهای کوچک تا خانه‌های معمارساز با الحاق، دک و بخش‌های کم‌شیب متنوع‌اند. ارزیابی مفید سقف از شکل واقعی ساختمان شروع می‌شود، نه یک توصیه سیستم واحد برای همه.",
    localContext:
      "پروژه‌های داخل شهر ونکوور ممکن است مشمول الزاماتی متفاوت از سایر نقاط بریتیش کلمبیا باشند. رولین پیش از نهایی کردن شرح کار، مرجع قانونی مرتبط با ملک و الزامات همان پروژه را بررسی می‌کند.",
    assessmentFocus: [
      "اتصال بین سقف اصلی، الحاق و بخش کم‌شیب",
      "دره‌ها، تقاطع دیوار، نفوذها و فلاشینگ اطراف",
      "مسیر زهکشی در سایت‌های شهری متراکم",
    ],
    propertyContexts: ["خانه تک‌خانواده", "خانه بازسازی و گسترش‌یافته", "سیستم سقف شیب ترکیبی"],
    faqs: [
      {
        question: "آیا پروژه سقف ونکوور همان قوانین بقیه بریتیش کلمبیا را دارد؟",
        answer:
          "همیشه نه. شهر ونکوور نظام مقررات ساختمان خود را دارد. الزامات بر اساس آدرس و نوع کار در مرحلهٔ برنامه‌ریزی تأیید می‌شود، نه با فرض از چک‌لیست منطقه‌ای.",
      },
      {
        question: "آیا یک خانه ممکن است به بیش از یک رویکرد سقف نیاز داشته باشد؟",
        answer:
          "بله. الحاق، ورودی و دک سقف شیب و اتصال متفاوت معرفی می‌کند. ارزیابی این پیوندها را بخشی از یک سیستم مدیریت آب می‌داند.",
      },
    ],
  },
  burnaby: {
    intro:
      "محله‌های مسکونی برنابی شامل زمین‌های مسطح، خیابان‌های شیبدار و خانه‌هایی است که با الحاق رشد کرده‌اند. این تنوع زهکشی، دسترسی ایمن و اتصال بین بخش‌های سقف را بخش مهمی از ارزیابی اولیه می‌کند.",
    localContext:
      "سقف برنابی بسته به میزان قرارگیری در معرض باد و باران، پوشش درختی، شیب و شکل ساختمان ممکن است متفاوت از سقف چند محله دورتر عمل کند. توصیه‌ها بر اساس شرایط مشاهده‌شده در ملک نوشته می‌شود.",
    assessmentFocus: [
      "شیب، دسترسی و محدودیت جابه‌جایی مصالح اطراف خانه",
      "زهکشی دره، پیش‌آمدگی، ناودان و بخش کم‌شیب",
      "اتصال جایی که الحاق به ساختمان اصلی می‌رسد",
    ],
    propertyContexts: ["املاک دامنه‌ای", "خانه با الحاق", "سایت‌های مسکونی بالغ"],
    faqs: [
      {
        question: "چرا دسترسی سایت برای تعیین شرح کار سقف مهم است؟",
        answer:
          "دسترسی بر حفاظت، جابه‌جایی مصالح و ترتیب کار اثر می‌گذارد. این باید پیش از نهایی شدن برنامه نصب و شرح کار بررسی شود.",
      },
      {
        question: "آیا ناودان‌ها باید با سقف بررسی شوند؟",
        answer:
          "زهکشی باید مسیر متصل از سطح سقف تا نقطه تخلیه مناسب در نظر گرفته شود. ارزیابی وضعیت ناودان و لوله باران قابل‌مشاهده را بدون فرض کار اختیاری ناودان ثبت می‌کند.",
      },
    ],
  },
  coquitlam: {
    intro:
      "خانه‌های کوکیتلام ممکن است در زمین‌های شیب‌دار و چندسطحی و نزدیک پوشش گیاهی انبوه قرار گرفته باشند. پیش از پیشنهاد تعمیر یا تعویض، وضعیت سقف، مسیر تجمع برگ و شاخه، زهکشی و شدت باران‌های همراه با باد را یکجا بررسی می‌کنیم.",
    localContext:
      "ارتفاع و میزان باد و باران در محله‌های نزدیک کوکیتلام نیز می‌تواند متفاوت باشد. بنابراین سیستم و جزئیات مناسب سقف باید برای همان ساختمان انتخاب شود، نه بر پایهٔ یک نسخهٔ عمومی برای کل شهر.",
    assessmentFocus: [
      "زهکشی سقف پایین از سطح بالایی در خانه چندطبقه",
      "دره و اتصال سقف به دیوار در اشکال پیچیده",
      "تجمع آوار اطراف ناودان، نفوذ و نقاط زهکشی",
    ],
    propertyContexts: ["خانه چندطبقه", "مسکن شیبدار", "خانه نزدیک پوشش گیاهی بالغ"],
    faqs: [
      {
        question: "آیا سقف پیچیده خودکار به تعویض نیاز دارد؟",
        answer:
          "خیر. پیچیدگی بر تشخیص و جزئیات اثر می‌گذارد، اما تعمیر و تعویض همچنان به این بستگی دارد که مشکل منزوی است یا در کل سیستم.",
      },
      {
        question: "آوار درخت در بازرسی چگونه در نظر گرفته می‌شود؟",
        answer:
          "ارزیابی مناطقی را که آوار تجمع یا زهکشی را کند می‌کند ثبت می‌کند. سپس توصیه نگهداری به آنچه در همان ملک دیده می‌شود مرتبط می‌شود.",
      },
    ],
  },
  surrey: {
    intro:
      "ساری مجموعهٔ متنوعی از محله‌های قدیمی و خانه‌های نوساز با سقف‌های بزرگ و یکپارچه دارد. مساحت سقف، میزان باد و باران و تعداد محل‌های اتصال، همگی بر شرح دقیق کار اثر می‌گذارند.",
    localContext:
      "توصیه‌ای که برای یک ملک ارائه می‌شود، نسخهٔ ثابت و مناسبی برای ملک دیگر نیست. رولین وضعیت همان سقف را مستند می‌کند و مزایا و محدودیت‌های هر گزینه را توضیح می‌دهد.",
    assessmentFocus: [
      "سطح‌های بزرگ و نحوه جریان آب بین آن‌ها",
      "فلاشینگ در دریچه سقفی، دیوار، دودکش و نفوذ",
      "آیا آسیب منزوی است یا در کل سیستم سقف تکرار می‌شود",
    ],
    propertyContexts: ["خانه تک‌خانواده بزرگ", "سقف چندسطحی جدید", "خانه‌های محله‌ای قدیمی"],
    faqs: [
      {
        question: "چه چیزی تعمیر و تعویض سقف بزرگ را تعیین می‌کند؟",
        answer:
          "فقط اندازه نیست. تصمیم به وضعیت پوشش، تعداد و محل خرابی، جزئیات اتصال و امکان تعمیر موضعی بادوام بستگی دارد.",
      },
      {
        question: "آیا می‌توان ارزیابی را به یک منطقه مشکل محدود کرد؟",
        answer:
          "مشکل گزارش‌شده دقیق بررسی می‌شود، اما سطح‌های مجاور و مسیر زهکشی هم باید دیده شوند چون آب پیش از دیده شدن در داخل حرکت می‌کند.",
      },
    ],
  },
  richmond: {
    intro:
      "محیط عموماً کم‌ارتفاع ریچموند زهکشی کنترل‌شده سقف را به‌ویژه در دوره‌های بارانی طولانی برجسته می‌کند. بخش‌های کم‌شیب، لبه‌های اطراف و مسیر تخلیه به همان دقت پوشش اصلی سقف نیاز دارند.",
    localContext:
      "شرایط ساحلی و باز بودن سایت می‌تواند بر نحوه رسیدن باران بادی به لبه و اتصال سقف اثر بگذارد. توصیه مصالح و جزئیات فقط پس از بررسی شیب، شکل و میزان قرارگیری در معرض باد و باران خانه تعیین می‌شود.",
    assessmentFocus: [
      "زهکشی خوب و نقاط پایین قابل‌مشاهده در سطح کم‌شیب",
      "لبه، فلاشینگ و اتصال در معرض باران شدید",
      "نحوه دریافت آب ناودان و لوله عمودی از سطح سقف",
    ],
    propertyContexts: ["بخش سقف کم‌شیب", "مسکن باز", "خانه تک‌خانواده مدرن"],
    faqs: [
      {
        question: "آیا سقف که مسطح به نظر می‌رسد واقعاً صاف است؟",
        answer:
          "سقف کم‌شیب همچنان باید آب را به سمت زهکش هدایت کند. ارزیابی مسیر زهکشی طراحی‌شده و نشانه‌هایی که آب طبق طراحی حرکت نمی‌کند را می‌یابد.",
      },
      {
        question: "آیا شرایط ساحلی یعنی فلز همیشه درست است؟",
        answer:
          "خیر. شیب سقف، طراحی ساختار سقف، مناسب بودن محصول، جزئیات و اولویت مالک همه مهم‌اند. مصالح فقط از موقعیت انتخاب نمی‌شود.",
      },
    ],
  },
  "north-vancouver": {
    intro:
      "خانه‌های ونکوور شمالی اغلب در محیط شیبدار، مرطوب و پوشیده از گیاه قرار دارند. دسترسی ایمن، رواناب متمرکز و آوار اطراف نقاط زهکشی قبل از تعیین شرح کار سقف ملاحظات عملی هستند.",
    localContext:
      "شرایط محله‌های کم‌ارتفاع با املاک نزدیک کوه در نورث‌شور یکسان نیست. ارزیابی، میزان بادگیری و دسترسی واقعی سقف را در محل ثبت می‌کند و به یک توصیف کلی منطقه بسنده نمی‌کند.",
    assessmentFocus: [
      "دسترسی ایمن به بخش‌های تند یا بلند سقف",
      "مسیر زهکشی حجیم بین سطوح سقف",
      "آوار و نگه‌داری رطوبت اطراف سقف سایه‌دار",
    ],
    propertyContexts: ["املاک تند", "خانه چندسطحی", "املاک مسکونی جنگلی"],
    faqs: [
      {
        question: "آیا سقف سایه‌دار به برنامه نگهداری متفاوت نیاز دارد؟",
        answer:
          "ممکن است. شرایط خشک و تجمع آوار بسته به جهت و پوشش درختی متفاوت است؛ راهنمای نگهداری باید بر مناطق سقف مشاهده‌شده باشد.",
      },
      {
        question: "سایت شیبدار چگونه بر پروژه اثر می‌گذارد؟",
        answer:
          "بر دسترسی، حفاظت و ترتیب کار اثر می‌گذارد. این محدودیت‌ها پیش از نهایی شدن شرح کار مستند می‌شوند، نه به‌عنوان غافلگیری حین کار.",
      },
    ],
  },
  "west-vancouver": {
    intro:
      "مسکن ونکوور غربی اغلب حجم پیچیده، شیشه گسترده، تراس و ترکیب اشکال سقف تند و کم‌شیب دارد. کار موفق سقف به حل اتصالات بدون از دست دادن نگاه معماری ساختمان بستگی دارد.",
    localContext:
      "املاک رو به دریا، دامنه‌ای و جنگلی می‌توانند در همان شهرداری شرایط محیطی متفاوت داشته باشند. رولین قبل از توصیه سیستم، شکل سقف، دسترسی و مسیر آب را ارزیابی می‌کند.",
    assessmentFocus: [
      "اتصال بین فلز معماری، غشا و بخش‌های شیبدار",
      "نرده، دک سقف، اتصال دیوار و زهکشی پنهان",
      "دسترسی و برنامه حفاظت اطراف فضای بیرونی تمام‌شده",
    ],
    propertyContexts: ["خانه معمارساز", "اقامتگاه دامنه‌ای", "سیستم سقف مصالح ترکیبی"],
    faqs: [
      {
        question: "آیا تعویض سقف می‌تواند ویژگی معماری خانه را حفظ کند؟",
        answer:
          "باید بخشی از توصیه باشد. پروفیل، رنگ، جزئیات لبه و اتصال بصری می‌تواند همراه با مناسب بودن فنی ارزیابی شود.",
      },
      {
        question: "چرا سیستم‌های سقف ترکیبی با هم ارزیابی می‌شوند؟",
        answer:
          "آب از مرز بین مناطق سقف عبور می‌کند. جزئیات مناسب یک سطح باید به ساختار سقف بعدی به‌طور قابل‌اتکا متصل شود.",
      },
    ],
  },
  "new-westminster": {
    intro:
      "نیو وست‌مینستر شامل خانه‌های قدیمی، بازسازی‌شده و سایت‌های مسکونی فشرده است که سقف ممکن است در چند نسل تغییر کرده باشد. کار اول درک ساختار سقف موجود و محل اتصال تغییرات بعدی است.",
    localContext:
      "سن به‌تنهایی وضعیت سقف را تعیین نمی‌کند. رولین در پوشش، فلاشینگ، اتصال و نقاط زهکشی شواهد می‌یابد و جزئیات قابل تعمیر را از مشکل سیستم گسترده جدا می‌کند.",
    assessmentFocus: [
      "لایه‌های تعمیر یا بازسازی گذشته روی سطح سقف",
      "اتصال الحاق، ورودی و سازه‌های متصل",
      "دسترسی مصالح و حفاظت همسایه در سایت فشرده",
    ],
    propertyContexts: ["خانه تک‌خانواده قدیمی", "خانه بازسازی‌شده", "سایت مسکونی متراکم"],
    faqs: [
      {
        question: "آیا خانه قدیمی همیشه به سقف کامل نیاز دارد؟",
        answer:
          "خیر. وضعیت قابل‌مشاهده و عملکرد ساختار سقف مهم‌تر از سن خانه است. ارزیابی مستند پیش از تصمیم لازم است.",
      },
      {
        question: "اگر چند وصله قبلی روی سقف باشد چه؟",
        answer:
          "کارهای گذشته بخشی از تشخیص مستند می‌شود. بازیابی مکرر در یک ناحیه می‌تواند نشان اتصال حل‌نشده باشد؛ وصله‌های نامرتبط ممکن است علت دیگری داشته باشند.",
      },
    ],
  },
  "port-coquitlam": {
    intro:
      "خانه‌های پورت کوکیتلام اغلب سقف شیبدار مستقیم را با فضای بیرونی، گاراژ، الحاق یا سقف‌های بعداً اضافه‌شده ترکیب می‌کنند. این بخش‌های فرعی نحوه رسیدن آب به دره، دیوار و ناودان را تغییر می‌دهند.",
    localContext:
      "نشت نزدیک الحاق لزوماً دقیقاً از بالای همان نقطه شروع نمی‌شود. ارزیابی سطح‌ها و اتصالات را پیش از تعریف منطقه تعمیر دنبال می‌کند.",
    assessmentFocus: [
      "اتصال بین سقف اصلی، گاراژ و الحاق بعدی",
      "بخش سقف پایین که آب از سطح بالایی می‌گیرد",
      "نفوذ و فلاشینگ در اتصال سقف و دیوار",
    ],
    propertyContexts: [
      "خانه با گاراژ متصل",
      "خانه تک‌خانواده گسترش‌یافته",
      "زهکشی سقف چندمرحله‌ای",
    ],
    faqs: [
      {
        question: "چرا نشت ممکن است دور از منبع ظاهر شود؟",
        answer:
          "آب می‌تواند روی پوشش، قاب یا اتصال سقف حرکت کند پیش از دیده شدن. تشخیص مسیر محتمل را دنبال می‌کند نه فقط لکه داخلی را به‌عنوان علت.",
      },
      {
        question: "آیا سقف گاراژ و ورودی در ارزیابی گنجانده می‌شود؟",
        answer:
          "ممکن است، به‌ویژه اگر به سقف اصلی متصل یا از آن زهکشی می‌گیرند. محدودهٔ ارزیابی درخواستی باید مناطق بررسی را مشخص کند.",
      },
    ],
  },
  "port-moody": {
    intro:
      "محیط مسکونی جنگلی رو به ورودی در پورت مودی می‌تواند رطوبت، آوار و شرایط محیطی متغیر را در جهت‌های مختلف یک سقف قرار دهد. یادداشت بازرسی باید این شرایط را بر اساس سطح سقف تفکیک کند.",
    localContext:
      "جهت سقف، پوشش گیاهی نزدیک و ارتفاع ساختمان می‌تواند به اندازه نام مصالح مهم باشد. رولین این متغیرها را پیش از بحث نگهداری، تعمیر یا تعویض مستند می‌کند.",
    assessmentFocus: [
      "باران بادی در لبه، دیوار و نفوذ",
      "جمع آوار در دره و نقاط زهکشی",
      "الگوهای فرسایش متفاوت در سطح سایه‌دار و آفتابی",
    ],
    propertyContexts: ["املاک رو به ورودی", "سایت جنگلی", "خانه تک‌خانواده چندسطحی"],
    faqs: [
      {
        question: "آیا جهت‌های مختلف یک سقف می‌توانند متفاوت پیر شوند؟",
        answer:
          "بله. آفتاب، سایه، باد و آوار بسته به جهت متفاوت است. گزارش وضعیت باید هر منطقه سقف را توصیف کند نه یک وضعیت برای کل سقف.",
      },
      {
        question: "آیا آوار سطحی نشانه آسیب سقف است؟",
        answer:
          "خیر. این مشاهده نگهداری و زهکشی است نه تشخیص جایگزین. پوشش و جزئیات پایه همچنان باید ارزیابی شوند.",
      },
    ],
  },
  "maple-ridge": {
    intro:
      "میپل ریج خانه‌های مسکونی بزرگ، محله‌های جنگلی و زمین‌های شیب‌دار فراوانی دارد؛ بنابراین میزان باد و باران و شرایط دسترسی از ملکی به ملک دیگر تفاوت چشمگیری دارد. سقف و محیط اطراف آن در قالب یک برنامهٔ یکپارچه بررسی می‌شوند.",
    localContext:
      "در مناطق بلندتر یا بازتر ممکن است شرایطی دیده شود که مناطق پایین‌تر را نشان نمی‌دهد. انتخاب سیستم و جزئیات به ملک مرتبط است نه فرض منطقه‌ای.",
    assessmentFocus: [
      "دسترسی، آماده‌سازی و حفاظت برای سایت بزرگ‌تر یا شیبدار",
      "مدیریت زهکشی و آوار نزدیک پوشش گیاهی بالغ",
      "میزان قرارگیری در معرض باد و باران در شیروانی، پیش‌آمدگی و لبه سقف",
    ],
    propertyContexts: ["سایت مسکونی بزرگ‌تر", "مناطق دامنه‌ای", "املاک جنگلی"],
    faqs: [
      {
        question: "آیا ملک بزرگ‌تر توصیه سقف را تغییر می‌دهد؟",
        answer:
          "به‌تنهایی خیر. شیب زمین می‌تواند بر دسترسی و برنامه‌ریزی پروژه اثر بگذارد، اما انتخاب سیستم مناسب سقف همچنان به شیب و ساختار سقف، میزان باد و باران و وضعیت موجود بستگی دارد.",
      },
      {
        question: "لبه‌های بیشتر در معرض چگونه بررسی می‌شوند؟",
        answer:
          "ارزیابی وضعیت لبه قابل‌مشاهده، مسائل مرتبط با تثبیت و جزئیات اتصال را مستند می‌کند و سپس به سیستم انتخاب‌شده و الزامات قابل‌اجرا مرتبط می‌کند.",
      },
    ],
  },
  delta: {
    intro:
      "دلتا از محله‌های ساحلی، زمین‌های کشاورزی و مناطق مسکونی دورتر از ساحل تشکیل شده است. تفاوت میزان باد و باران در این نواحی باعث می‌شود وضعیت بادگیری، زهکشی و شکل سقف هر ملک پیش از انتخاب راه‌حل به‌دقت بررسی شود.",
    localContext:
      "املاک تساواسن، لادنر یا نورت دلتا نباید فقط بر اساس نام شهرداری توصیه بگیرند. رولین شرح کار را بر اساس ساختمان و شرایط مشاهده‌شده در آدرس تعیین می‌کند.",
    assessmentFocus: [
      "قرارگیری در معرض باد در پیش‌آمدگی، شیروانی و جزئیات اطراف",
      "ظرفیت زهکشی و تخلیه در سطح‌های گسترده",
      "وضعیت مصالح در نماهای با شرایط محیطی متفاوت",
    ],
    propertyContexts: ["مناطق ساحلی", "مسکن باز", "خانه تک‌خانواده داخلی"],
    faqs: [
      {
        question: "آیا یک سیستم سقف برای کل دلتا مناسب است؟",
        answer:
          "نمی‌توان خودکار کل شهر را انتخاب کرد. شیب، میزان قرارگیری در معرض باد و باران، معماری، زهکشی و بودجه همگی بر سیستم مناسب اثر می‌گذارند.",
      },
      {
        question: "پس از طوفان باد چه بررسی شود؟",
        answer:
          "ارزیابی هدفمند می‌تواند تغییرات قابل‌مشاهده در لبه، پوشش، فلاشینگ و نفوذهای در دسترس را بررسی کند. ایمنی اولویت است؛ مالک نباید روی سقف خیس یا آسیب‌دیده بالا رود.",
      },
    ],
  },
  langley: {
    intro:
      "املاک لنگلی می‌توانند خانه تک‌خانواده بزرگ‌تر، سطح وسیع سقف و چند سازه متصل داشته باشند. شرح کار روشن هر منطقه سقف را جدا می‌کند و نحوه زهکشی و اتصال را حل می‌کند.",
    localContext:
      "سایت باز و مناطق درختی ملاحظات نگهداری و شرایط محیطی متفاوتی دارند. رولین شرایط واقعی سایت را پیش از توصیه مصالح یا محدودیت تعمیر ثبت می‌کند.",
    assessmentFocus: [
      "سطح گسترده و مسیر زهکشی طولانی",
      "اتصال بین خانه، گاراژ و فضای بیرونی سقف‌دار",
      "تفاوت بین مناطق باز و محافظت‌شده با درخت",
    ],
    propertyContexts: ["خانه تک‌خانواده بزرگ‌تر", "خانه با سازه‌های متصل", "مسکن باز"],
    faqs: [
      {
        question: "آیا می‌توان پروژه را بر اساس سطح سقف مرحله‌بندی کرد؟",
        answer:
          "گاهی فقط وقتی ساختار سقف و اتصال نقطه توقف بادوام اجازه می‌دهد. شرح کار باید توضیح دهد مرحله‌بندی چگونه بر مناطق متصل اثر می‌گذارد.",
      },
      {
        question: "آیا سطح بزرگ به یک مصالح خاص نیاز دارد؟",
        answer:
          "خیر. شکل یک ورودی است. شیب، طراحی ساختار سقف، میزان قرارگیری در معرض باد و باران، ظاهر، اولویت نگهداری و بودجه با هم در نظر گرفته می‌شوند.",
      },
    ],
  },
  "white-rock": {
    intro:
      "خانه‌های وایت راک می‌توانند میزان قرارگیری در معرض باد و باران رو به دریا را با اشکال سقف مدرن، بالکن و بخش‌های کم‌شیب ترکیب کنند. لبه، اتصال دیوار و جزئیات زهکشی در بافت آن معماری بررسی می‌شوند.",
    localContext:
      "شرایط ساحلی به این معنی نیست که یک محصول سقف برای همه درست است. مناسب بودن محصول، طراحی سیستم و جزئیات قابل‌مشاهده همراه با شیب و اولویت مالک در نظر گرفته می‌شود.",
    assessmentFocus: [
      "جزئیات اطراف و اتصال در معرض باران متمرکز",
      "زهکشی کم‌شیب اطراف نرده، کرنیز و دیوار مجاور",
      "سازگاری ساختار سقف انتخاب‌شده با جزئیات فلزی قابل‌مشاهده",
    ],
    propertyContexts: ["خانه رو به دریا", "اقامتگاه مدرن", "طراحی سقف شیب ترکیبی"],
    faqs: [
      {
        question: "آیا قرارگیری در معرض هوای دریا خودکار مصالح را حذف می‌کند؟",
        answer:
          "خودکار نه. کل سیستم، محدودیت محصول، پوشش یا پرداخت، تثبیت و جزئیات اتصال باید برای همان ملک بررسی شوند.",
      },
      {
        question: "چرا اتصال بالکن و نرده مهم است؟",
        answer:
          "می‌تواند جریان ساده آب را مختل کند و اتصال دیوار، کرنیز یا غشا معرفی کند. این رابط‌ها باید در ارزیابی مستند گنجانده شوند.",
      },
    ],
  },
} as const satisfies Record<ServiceAreaSlug, ServiceAreaContentTranslation>;

export const ui = {
  common: {
    home: "خانه",
    services: "خدمات",
    serviceAreas: "مناطق خدماتی",
    contact: "تماس",
    faq: "پرسش‌های متداول",
    learnMore: "بیشتر بدانید",
    requestAssessment: "درخواست ارزیابی سقف",
    exploreServices: "خدمات سقف را ببینید",
    viewContactOptions: "همه گزینه‌های تماس",
  },
  serviceDetail: {
    breadcrumbLabel: "مسیر",
    call: "تماس",
    whoForEyebrow: "این خدمت برای چه کسی است",
    whoForTitle: "ابتدا وضعیت سقف، نه محصول",
    warningSignsTitle: "علائم هشدار رایج",
    assessmentEyebrow: "انضباط ارزیابی",
    evaluatesTitle: "آنچه رولین ارزیابی می‌کند",
    solutionsEyebrow: "دسته راه‌حل‌ها",
    solutionsTitle: "گزینه‌ها از وضعیت سقف پیروی می‌کنند، نه از متن فروش",
    repairEyebrow: "وقتی تعمیر منطقی است",
    replaceEyebrow: "وقتی تعویض معنا دارد",
    scopeEyebrow: "شرح کار",
    scopeTitle: "چه چیزی بر کار اثر می‌گذارد",
    scopeBody:
      "شرح واقعی کار پس از ارزیابی مشخص می‌شود. رولین روفینگ قیمت، عمر یا ساختار سقف یکسان برای همه منتشر نمی‌کند چون به ساختمان و سیستم انتخاب‌شده بستگی دارد.",
    questionsEyebrow: "پرسش‌ها",
    questionsTitle: "پیش از تصمیم",
    nextStepEyebrow: "گام بعد",
    nextStepTitle: "درخواست ارزیابی روشن سقف",
    nextStepBody:
      "آنچه می‌بینید و محل ملک را بگویید. از این زمینه برای برنامه‌ریزی گفت‌وگوی مناسب استفاده می‌کنیم.",
  },
  servicesIndex: {
    eyebrow: "خدمات سقف",
    title: "شرح کار درست با تشخیص درست آغاز می‌شود",
    body: "دسته‌های اصلی خدمات رولین روفینگ را ببینید. هر کدام از ساختمان، سیستم سقف و شرایطی که می‌بینیم شروع می‌شود: نه از توصیه محصول از پیش تعیین‌شده.",
    exploreService: "مشاهده خدمت",
    technicalEyebrow: "مرجع فنی",
    technicalTitle: "منابع فعلی بریتیش کلمبیا مهم‌اند",
    rcabcTitle: "راهنمای عمل سقف RCABC",
    rcabcBody: "مرجع فعلی استاندارد سقف؛ پیوند به معنای عضویت رولین یا RoofStar نیست.",
    bcCodesTitle: "کدهای بریتیش کلمبیا",
    bcCodesBody: "مقررات استانی، اصلاحیه‌ها و اطلاعیه‌ها برای حوزه و شرح کار پروژه.",
    vancouverBylawTitle: "مقررات ساختمان ونکوور",
    vancouverBylawBody: "الزامات ویژه ونکوور جدا از کد استانی بررسی می‌شود.",
  },
  areaIndex: {
    breadcrumbLabel: "مسیر",
    eyebrow: "مترو ونکوور و لوئر مینلند",
    title: "مناطق خدمات سقف",
    body: "رولین روفینگ در جوامع زیر ارزیابی سقف مسکونی ارائه می‌دهد. هر پروژه برای شکل سقف، میزان قرارگیری در معرض باد و باران، دسترسی و الزامات محلی ملک برنامه‌ریزی می‌شود.",
    coverageEyebrow: "پوشش، نه شعبه",
    coverageTitle: "زمینه محلی بدون ادعای دفتر در هر شهر",
    coverageBody:
      "این صفحات می‌گویند ارزیابی سقف کجا ممکن است. ادعا نمی‌کنند رولین روفینگ در هر شهر فهرست‌شده دفتر دارد. برای تأیید پوشش پروژه با آدرس ملک تماس بگیرید.",
    nearbyEyebrow: "جامعه نزدیک؟",
    nearbyTitle: "از ما بخواهید پوشش پروژه را تأیید کنیم.",
    contactCta: "تماس با رولین روفینگ",
  },
  serviceArea: {
    breadcrumbLabel: "مسیر",
    metroVancouver: "مترو ونکوور",
    lowerMainland: "لوئر مینلند",
    roofingServicesSuffix: "خدمات سقف",
    titlePrefix: "خدمات سقف در {area}",
    confirmedCommunity:
      "{area} جامعه تأیید‌شده خدمات است. این صفحه منطقه خدمات است، نه ادعای شعبه محلی.",
    localContextEyebrow: "زمینه سقف محلی",
    localContextTitle: "ملک توصیه را تعیین می‌کند",
    propertyContextsLabel: "زمینه‌های ملکی که در برنامه‌ریزی در نظر می‌گیریم",
    prioritiesEyebrow: "اولویت‌های ارزیابی",
    prioritiesTitle: "در {area} به چه چیزی توجه شود؟",
    planningDisclaimer:
      "این اطلاعات برنامه‌ریزی عمومی است، نه تشخیص از راه دور یا نظر مهندسی. یافته‌ها و الزامات برای ملک مشخص تأیید می‌شود.",
    relatedEyebrow: "خدمات مرتبط",
    relatedTitle: "تصمیم‌های سقف برای خانه‌های {area}",
    relatedBody:
      "از مشکل مشاهده‌شده و وضعیت سقف شروع کنید. گام بعد می‌تواند تعمیر موضعی، بازرسی پیشگیرانه یا تعویض برنامه‌ریزی‌شده باشد.",
    fallbackServiceName: "خدمت سقف",
    faqSuffix: "پرسش‌های متداول",
    faqTitle: "پرسش‌ها پیش از ارزیابی",
    requestEyebrow: "درخواست ارزیابی",
    requestTitle: "از سقفی که پیش روی ماست شروع کنیم.",
    requestBody:
      "ملک در {area}، بخش‌های سقف و آنچه دیده‌اید را بگویید. این به آماده‌سازی ارزیابی کمک می‌کند؛ جایگزین بازدید در محل نیست.",
  },
  about: {
    eyebrow: "شرکت گروه رولین",
    title: "تخصص سقف با نگاه یک شرکت ساختمانی",
    body: "رولین روفینگ بخش تخصصی سقف گروه رولین است و ظرافت معماری، فرآیند شفاف و تفکر منضبط پروژه برند اصلی را به ساختار سقف می‌آورد.",
    relationshipEyebrow: "رابطه",
    relationshipTitle: "متخصص سقف. متصل به کل ساختمان.",
    relationshipBody1:
      "سقف سطح جدا نیست. دیوار، نما، نورگیر، تهویه، زهکشی و ساختار داخلی ساختمان را تکمیل می‌کند. رولین روفینگ به این رابط‌ها با زمینه ساخت گسترده‌تر نزدیک می‌شود.",
    relationshipBody2:
      "این رویکرد تشخیص و شرح کار روشن را پشتیبانی می‌کند. جایگزین نیاز به مهندس، طراح یا متخصص واجد شرایط وقتی موضوع خارج از کار سقف است نمی‌شود؛ به شناسایی زودهنگام محدودیت‌ها کمک می‌کند.",
    principlesEyebrow: "اصول کار",
    principlesTitle: "وضوح در هر نقطه تصمیم",
    assessTitle: "پیش از توصیه ارزیابی کنید",
    assessBody:
      "علائم قابل‌مشاهده، منبع محتمل و وضعیت کلی ساختار سقف پیش از پیشنهاد شرح کار در نظر گرفته می‌شود.",
    architectureTitle: "به معماری احترام بگذارید",
    architectureBody:
      "مصالح، رنگ، پروفیل، لبه و اتصال بر خواند ساختمان اثر می‌گذارد: نه فقط مسیر آب.",
    limitsTitle: "مرزها را بیان کنید",
    limitsBody:
      "فرض‌ها، استثناها و شرایطی که نیاز به بررسی بیشتر دارند در گفت‌وگو مطرح و در شرح کار درج می‌شوند.",
    parentEyebrow: "شرکت مادر",
    parentTitle: "گروه رولین را ببینید",
    parentBody: "برای بازسازی، ساخت و کار ساختمان گسترده‌تر، به سایت شرکت مادر بروید.",
    parentCta: "بازدید از RevolinGroup.com",
  },
  beforeAfter: {
    eyebrow: "قبل و بعد",
    title: "گالری قبل و بعد سقف در مترو ونکوور",
    body: "گالری‌های قبل و بعد رولین روفینگ را برای صاحبان خانه در مترو ونکوور و لوئر مینلند ببینید؛ از تعمیر فوری نشتی تا تعویض سقف‌های آسفالتی، فلزی، سدار و تخت. هر گالری، مسئله، روند ارزیابی و نتیجهٔ نهایی را با توضیحی روشن نشان می‌دهد.",
    notice:
      "با فیلتر خدمت، نوع سقف یا شهر، نزدیک‌ترین تحول به خانه خود را پیدا کنید و برای راهنمایی متناسب با ملک، ارزیابی سقف درخواست دهید.",
    projectHeroBody:
      "نمونه‌های قبل و بعد رولین روفینگ را در مترو ونکوور ببینید؛ از تعمیر فوری نشتی تا تعویض کامل سقف‌های آسفالتی، فلزی، سدار و تخت. هر گالری، مسئله، ارزیابی و نتیجهٔ نهایی را برای شرایط رایج خانه‌های لوئر مینلند توضیح می‌دهد.",
  },
  faqPage: {
    eyebrow: "راهنمای مالک",
    title: "پرسش‌های سقف، بدون متن فروش",
    body: "اطلاعات عملی درباره ارزیابی، تعمیر، تعویض و سیستم‌های سقف. مشاوره پروژه‌ای پس از بازدید در محل انجام می‌شود.",
  },
  contactPage: {
    eyebrow: "تماس با رولین روفینگ",
    title: "آنچه می‌بینید را بگویید",
    body: "موقعیت ملک، نوع سقف اگر می‌دانید و نگرانی یا پروژه‌ای که در نظر دارید را به اشتراک بگذارید. فرم فقط وقتی تحویل را تأیید می‌کند که سامانهٔ ثبت درخواست پیکربندی‌شده آن را بپذیرد.",
    directEyebrow: "تماس مستقیم",
    directTitle: "از اصول شروع کنید",
    phone: "تلفن",
    email: "ایمیل",
    serviceRegion: "منطقه خدمات",
    regionValue: "مترو ونکوور و لوئر مینلند",
  },
  legal: {
    legal: "حقوقی",
    privacyPolicy: "سیاست حریم خصوصی",
    websiteTerms: "شرایط استفاده از وب‌سایت",
    privacyBody: "نحوه رسیدگی به پرسش‌های وب‌سایت، داده ملک و ذخیره‌سازی ضروری مرورگر.",
    termsBody:
      "قوانینی که هنگام استفاده از وب‌سایت و مطالب آموزشی عمومی رولین روفینگ اعمال می‌شود.",
    lastUpdated: "آخرین به‌روزرسانی: ۱۰ اوت ۲۰۲۶.",
  },
} as const;

export type ContentUi = typeof ui;

// Semantic aliases used by the locale-content registry.
export const serviceContent = services;
export const serviceAreaContent = serviceAreas;

const content = { services, serviceAreas, ui } as const;
export default content;
